import { stopActiveTrailer } from '@/lib/trailer'
import { reportMediaError } from '@/lib/mediaErrors'
import { back, backToDetail, detailOverHome, focusRail, navigate, stackDepth, type Screen } from '@/app'
import {
  type ContentCard,
  type Episode,
  getContent,
  nextEpisode,
  previousEpisode,
} from '@/lib/catalog'
import { enterFullscreen, exitFullscreen, onFullscreenExit } from '@/lib/compat'
import { clear, el, setClass, setText, translate } from '@/lib/dom'
import type { RemoteKey } from '@/lib/keys'
import { languageNative } from '@/lib/language'
import { getLiveNow, type LivePlaybackResult, requestLivePlayback, LIVE_HEARTBEAT_MS, sendLiveHeartbeat } from '@/lib/live'
import {
  endPlaybackSession,
  openPlaybackSession,
  PROGRESS_INTERVAL_MS,
  type PlaybackResult,
  type PlaybackSessionHandle,
  type PlaybackTicket,
  reportWatchProgress,
  requestTicket,
} from '@/lib/playback'
import { resumeEpisode } from '@/lib/continue'
import { applyViewerAgeLimit, readSelectedProfileId, writeSelectedProfileId } from '@/lib/profiles'
import { hasDrm, loadShaka, type ShakaPlayerLike, type ShakaTrack, toShakaServers } from '@/lib/shaka'
import {
  hasMediaSource,
  loadHlsJs,
  resolveTrailer,
  resolveVimeoTrailer,
  type HlsLike,
  type TrailerSource,
} from '@/lib/trailer'
import { T } from '@/lib/i18n'
import { AGE_GATE_FROM, evaluateAge, loadAgeIfNeeded, trailerAllowed } from '@/lib/ageGate'
import { IS_BROWSER } from '@/lib/platform'
import { canPlayProtected, isTvUserAgent } from '@/lib/drmSupport'
import { capHlsLevels, SHAKA_TV_RESTRICTIONS } from '@/lib/videoCap'
import { authStatus, readSession, sessionGeneration } from '@/lib/session'
import { PAUSED_SESSION_RELEASE_MS } from '@/lib/pausedSession'
import { createWatchOutro, type WatchOutro } from '@/ui/watchOutro'

/**
 * Player ekranı — İKİ REJİM:
 *
 *   1. TREYLER (`trailerAsset` verilir, `contentId` yox) — publik Mux HLS, DRM
 *      lazım deyil. Native `<video>` + HLS; masaüstü dev-də hls.js CDN fallback.
 *
 *   2. TAM FİLM (`contentId` verilir) — `POST /api/playback/ticket` çağırılır:
 *        · 200 + DRM-siz manifest → native `<video>` (TV doğma HLS oynadır).
 *        · 200 + DRM manifest (Widevine/PlayReady) → Shaka CDN-dən yüklənir (lib/shaka.ts).
 *        · 401/403/«hazır deyil» → treylerə degrade + səbəb banneri (qara ekran yox, §6).
 *
 * ⚠ CANLI SEANSDA SEEK QADAĞANDIR (§5.13) — `liveSession` ilə ox düymələri bloklanır.
 */

export interface PlayerOptions {
  slug: string
  title: string
  liveProgramId?: string
  /** Tam film — bilet bu kontent üçün istənilir. Yoxdursa yalnız treyler rejimi. */
  contentId?: string | null
  /** Verilsə treyler mənbəyi (`mux:{id}`). Tam filmdə degrade fallback-ı kimi də işlənir. */
  trailerAsset?: string | null
  /** Treylerin başlanğıc saniyəsi — modaldan tam ekrana keçəndə verilir. */
  trailerStartAt?: number
  /**
   * Tam ekran ANA EKRANIN hero-sundan açılıb — çıxış ana ekranadır, modala yox
   * (sahib tələbi 2026-08-25). Ətraflı izah `app.ts`-dəki eyni adlı sahədədir.
   */
  fromHero?: boolean
  /**
   * Kontentin yaş həddi — fallback/tam ekran treyleri `trailerAllowed` ilə yoxlanılır
   * (2026-10-25). Verilməyibsə 0 sayılır (saytdakı `trailerAllowed` ilə eyni).
   */
  ageLimit?: number | null
  /** Serial bölümü — bilet bu bölüm üçün istənilir (filmdə verilmir). */
  episodeId?: string | null
  /** Serialın bütün bölümləri — bölüm bitəndə növbətisi buradan tapılır. */
  episodes?: Episode[]
  /**
   * «Oxşar» siyahısı — filmin sonluq qatındakı təkliflər (sahib tələbi
   * 2026-08-21: «youtube kimi film önəriləri»).
   *
   * ⚠ MODALIN CAVABINDAN GƏLİR, AYRICA SORĞU YOXDUR: `getContent` onsuz da
   *   `similar` qaytarır — player-dən ikinci gediş TV-də mənasız gecikmədir.
   */
  similar?: ContentCard[]
  /**
   * Sonluq qatı filmin bitməsinə neçə saniyə qalmış açılsın —
   * `cnt.contents.outro_seconds` (V064). Boşdursa [OUTRO_DEFAULT_SECONDS].
   */
  outroSeconds?: number | null
  /** Canlı seans — seek qadağandır, hamı eyni kadrı görür. */
  liveSession?: boolean
  /**
   * «Canlı» xətti kanal — bilet `POST /api/playback/live/ticket`-dən (kontent cədvəldən gəlir),
   * offset-ə seek edilir, playhead server saatına pin olunur (drift). Play/pause/seek bloklu.
   */
  live?: boolean
}

/** Player chrome-u bu qədər hərəkətsizlikdən sonra gizlənir (sahib tələbi: 5 saniyə). */
const CHROME_IDLE_MS = 5000

/**
 * Chrome qayıtdıqdan sonra klikin «yalnız göstər» sayıldığı pəncərə (ms) —
 * yalnız brauzer variantında (izah `bindPointer`-in klik blokundadır).
 *
 * ⚠ 700 ms ÖLÇÜLMÜŞ DƏYƏRDİR, təxmin deyil: siçan hərəkəti ilə onun ardınca
 *   gələn klik arasındakı məsafə insan əlində 100–300 ms-dir; 700 ms rahat pay
 *   verir, amma «filmi dayandırım» niyyəti ilə edilən ikinci klikdən (adətən
 *   1 saniyədən sonra) uzaqdır.
 */
const REVEAL_GRACE_MS = 700

/**
 * TELEVİZORDA BAŞLANĞIC BANT TƏXMİNİ (bit/s) — Android TV-dəki
 * `TvPlayerScreen.kt → TV_STARTUP_BANDWIDTH = 10_000_000` ilə eyni dəyər (522).
 *
 * ⚠ NİYƏ 6 YOX, 10 MBİT (Android-də ölçülmüş səbəb): ABR təxminin yalnız
 *   ~0.7-sini büdcə sayır — 6 Mbit → 4.2 Mbit büdcə deməkdir və 1080p pilləsi
 *   (ölçüldü: 3.0–4.2 Mbit/s) həddin lap kənarında qalır, açılış çox vaxt
 *   720p-dən olur. 10 Mbit → 7 Mbit büdcə: 1080p birinci seqmentdən seçilir.
 * ⚠ TV SƏTHİDİR: şəbəkə sabitdir (ethernet/ev Wi-Fi), ekran böyükdür. MƏCBURİ
 *   KEYFİYYƏT DEYİL — yalnız BAŞLANĞIC təxminidir, şəbəkə zəifdirsə ABR ilk
 *   seqmentdən sonra real ölçmə ilə enir (bufer riski yaranmır).
 * ⚠ Fon (modal) treyleri bu koddan keçmir (`lib/trailer.ts`, 6 Mbit qalır) —
 *   Android-də də hero treyleri ayrı yoldur (`HeroTrailer.kt`).
 */
const TV_STARTUP_BANDWIDTH = 10000000

/**
 * Treyler-fallback izahının ekranda qalma müddəti (ms) — Android
 * `TvPlayerScreen.kt → NOTICE_VISIBLE_MS = 5_000` (sahib tələbi 2026-08-06:
 * «playerdə artıq sözlərə gərək yoxdur» — izah ilk saniyələrdə görünür,
 * sonra kadr TƏMİZ qalır).
 */
const BANNER_VISIBLE_MS = 5000

/**
 * «Növbəti bölüm N saniyə sonra» geri sayımı (MYTasks 547 — sayt pariteti:
 * `WatchClient.tsx → NEXT_EPISODE_SECONDS = 5`).
 *
 * ⚠ SAYIM YALNIZ `ended`-DƏN SONRA BAŞLAYIR — sahibin köhnə şərti («serial tam
 *   bitməmiş keçirmə, qoy bitsin sonra keçir») POZULMUR: bölüm onsuz da bitib,
 *   sayım yalnız «indi keçirəm» xəbərdarlığıdır. Əvvəl keçid ANİ idi və
 *   istifadəçinin dayandırmağa imkanı yox idi (Android TV-də də belədir — bu
 *   bənd məhz SAYTDAN götürülür).
 */
const NEXT_EPISODE_SECONDS = 5

/**
 * SONLUQ QATININ DEFOLT ANI (saniyə) — redaksiya `cnt.contents.outro_seconds`
 * sütununu (V064) doldurmayanda işlədilir.
 *
 * ⚠ 30 SANİYƏ — SAHİBİN 2026-08-21 TƏLƏBİ («film bitməzdən 30 san öncə
 *   başlamalıdır (fallback)»). Sayt (`WatchClient.tsx`) və Android TV
 *   (`TvPlayerScreen.kt`) da eyni dəyərdədir — əvvəl üçü fərqli idi (20/40/yox).
 * ⚠ NİYƏ DEFOLT VAR: kataloqda minlərlə film var, hamısını əl ilə doldurmaq
 *   günlər aparardı — o vaxta qədər özəllik HEÇ BİR filmdə işləməzdi.
 */
const OUTRO_DEFAULT_SECONDS = 30

/**
 * Sarıma basışlarının BİRLƏŞDİRİLMƏ pəncərəsi (ms) — MYTasks 547.
 *
 * ⚠ NİYƏ LAZIM OLDU: hər ox basışı BİRBAŞA `video.currentTime`-a yazılırdı.
 *   Pult avtotəkrarı saniyədə ~11 addım verir (PERFORMANCE.md §2), yəni 3 saniyə
 *   basılı saxlamaq 33 AYRI seek deməkdir — hər biri hls.js/Shaka-da bufer
 *   sıfırlaması + yeni seqment sorğusudur. TV-də nəticə: ekran donur, səs kəsilir
 *   və istifadəçi haraya getdiyini görmür. İndi basışlar toplanır, ekranda
 *   «+40 san» göstərilir və `currentTime` YALNIZ son basışdan 400 ms sonra bir
 *   dəfə yazılır (sayt `media-time-range` sürüklənməsinin pult qarşılığı).
 */
const SEEK_COMMIT_MS = 400
// 2026-10-04: gecikmiş açılış da bağlanır; yeni bölüm/sınaq əvvəlki sessiyanın
// bağlanmasını gözləyir. Media hazırlanması bu növbədən asılı deyil.
let playbackClosing: Promise<void> = Promise.resolve()

/** Player başlığı: «Serial adı · N-ci bölüm — Bölüm adı». */
export function episodeTitle(seriesTitle: string, episode: Episode): string {
  const label = episode.number + T('-ci bölüm')
  return seriesTitle + ' · ' + (episode.title || label)
}

/** Player ekranı (treyler · tam film · canlı) — bax faylın başındakı izah. */
export function createPlayerScreen(options: PlayerOptions): Screen {
  stopActiveTrailer()
  // ⚠ HƏQİQİ FULLSCREEN (2026-08-02, istifadəçi tələbi): əvvəl «tam ekran»
  //   yalnız CSS-lə 1920×1080 kətanı doldururdu — brauzer chrome-u (ünvan
  //   çubuğu/tab-lar) görünürdü. İndi brauzerin öz Fullscreen API-si də
  //   çağırılır. Ekran mount olunan kimi (pult/siçan aksiyasının birbaşa
  //   zəncirində) çağırılmalıdır ki, «istifadəçi jesti» şərti ödənsin.
  enterFullscreen(document.documentElement)

  const node = el('div', 'screen')
  const page = el('div', 'player')
  // ⚠ CANLIDA MENYU BAŞLANĞICDAN GÖRÜNÜR (`app.ts` → `mount`: rail yalnız adi
  //   player-də gizlədilir), ona görə player elə İLK ANDAN onun yanına çəkilir.
  //   Qoyulmasaydı rail (z-index 20) canlı yayının sol kənarını örtərdi.
  if (options.live) setClass(page, 'is-railed', true)
  node.appendChild(page)

  const video = document.createElement('video')
  video.className = 'player__video'
  video.setAttribute('playsinline', '')
  page.appendChild(video)

  const ui = el('div', 'player__ui')
  page.appendChild(ui)

  // Başlıq: CANLIDA gizli (film adı sol-altda ayrıca göstərilir, «Treyler» yazılmır);
  // treyler rejimində «Treyler ·» prefiksi qalır.
  const heading = el(
    'h1',
    'player__title',
    options.contentId || options.live ? options.title : T('Treyler') + ' · ' + options.title,
  )
  if (options.live) heading.style.display = 'none'
  page.appendChild(heading)

  // ── Canlı chrome (bütün TV-lərdə eyni): CANLI sol-üst · «kinobox» sağ-üst · film adı sol-alt ──
  if (options.live) {
    const shadow = 'text-shadow:0 2px 10px rgba(0,0,0,0.75)'

    const badge = el('div', 'player__live-tag', T('● CANLI'))
    badge.style.cssText = `position:absolute;left:52px;top:44px;z-index:6;font-size:20px;font-weight:700;color:#e11d2a;${shadow}`
    page.appendChild(badge)

    // kinobox loqosu — sağ-üst küncdə brend wordmark ŞƏKLİ (mətn deyil; saytla eyni logo.svg).
    // `<img src>` köhnə TV brauzerlərində də (Chrome 38) dekodlanır — inline SVG kimi reflow yaratmır.
    const logo = document.createElement('img')
    logo.src = 'logo.svg'
    logo.alt = 'kinobox'
    logo.style.cssText =
      'position:absolute;right:52px;top:40px;z-index:6;height:36px;width:auto;opacity:0.92;filter:drop-shadow(0 2px 10px rgba(0,0,0,0.75))'
    page.appendChild(logo)

    const name = el('div', 'player__live-name', options.title)
    name.style.cssText = `position:absolute;left:52px;bottom:48px;z-index:6;font-size:26px;font-weight:600;color:#fff;${shadow}`
    page.appendChild(name)
  }

  const bar = el('div', 'player__bar')
  /**
   * BUFERLƏNMİŞ HİSSƏ — zolağın üçüncü qatı (fon → bufer → oynanmış).
   *
   * ⚠ HƏR İKİ ETALONDA VAR, veb TV-də YOX İDİ (547 auditi): saytda Mux-un
   *   `media-time-range`-i, Android TV-də Media3-ün `DefaultTimeBar`-ı bufer
   *   aralığını ayrıca boya ilə çəkir. Onsuz istifadəçi «filmin bu qədəri
   *   yüklənib» məlumatını görmür və hər dayanmanı nasazlıq sayır.
   * ⚠ `width` YAZILIR, amma yalnız `progress` hadisəsində və yalnız DƏYİŞƏNDƏ
   *   (aşağıdakı `lastBufferedWidth`) — zolaq 8px hündürlükdə mütləq mövqeli
   *   qutudur, ölçüsü qonşu layout-a təsir etmir (PERFORMANCE.md §7 qadağası
   *   ANİMASİYA edilən `width`-ə aiddir; burada animasiya yoxdur).
   */
  const buffered = el('div', 'player__buffered')
  bar.appendChild(buffered)
  const fill = el('div', 'player__fill')
  bar.appendChild(fill)
  page.appendChild(bar)

  // ── Vaxt göstəriciləri (sahib bildirişi 2026-08-03: «playerin özəllikləri tam
  //    görünmür») ───────────────────────────────────────────────────────────────
  // Əvvəl chrome-da YALNIZ başlıq + zolaq + izah var idi: istifadəçi neçənci
  // dəqiqədə olduğunu və filmin nə qədər qaldığını GÖRMÜRDÜ. Zolağın hər iki
  // ucunda vaxt yazısı adi TV player davranışıdır.
  // ⚠ İKİSİ DƏ BİR QABIN İÇİNDƏ, YAN-YANA («1:23 / 45:07») — sahib düzəlişi
  //   2026-08-03: «youtube kimi olsun». Əvvəl vaxtlar zolağın İKİ UCUNA mütləq
  //   mövqe ilə yayılmışdı; sağdakı yazı tam ekran düyməsi ilə eyni küncü paylaşır
  //   və ikisi bir-birinə dəxli olmayan iki ada kimi görünürdü.
  // ⚠ QAB AXIN İÇİNDƏDİR (`inline-block` uşaqlar): yazının eni dəyişəndə (0:59 →
  //   1:00:59) ayırıcı və ümumi müddət özləri sürüşür. Mütləq mövqe ilə hər ölçü
  //   dəyişməsində əl ilə hesablama lazım olardı.
  // ⚠ İDARƏ SƏTRİ BİR AXIN QABINDADIR (sahib düzəlişi 2026-08-04): «10 san önə
  //   arxaya məsələsi playerin ən sağında yox time göstərən hissənin sağında
  //   olmalıdır» + «vaxt göstərənin solunda da play pause işarəsi olsun».
  //   Sıra: ⏯ · «1:23 / 45:07» · ⟲10 · ⟳10.
  // ⚠ NİYƏ AXIN, NİYƏ SABİT `left` DEYİL: vaxt yazısının eni film uzunluğu ilə
  //   dəyişir (0:59 → 1:23:45 / 2:10:00). Sabit mövqedə ±10 düymələri qısa filmdə
  //   yazıdan aralı, uzun filmdə isə ÜSTÜNDƏ dururdu. Axında qab özü uyğunlaşır.
  const controls = el('div', 'player__controls')

  /**
   * Oynat/dayandır — vaxtın SOLUNDA (sahib tələbi 2026-08-04).
   *
   * ⚠ İKİ İKON QABAQCADAN QURULUR, sadəcə `display` çevrilir: hər vəziyyət
   *   dəyişməsində `innerHTML` yazmaq SVG-ni yenidən parse etdirir (PERFORMANCE.md
   *   — köhnə TV-də bu, kadr atlaması deməkdir).
   */
  const playToggle = el('div', 'focusable player__ctl')
  const iconPlay = el('span', 'player__ctl-icon')
  iconPlay.innerHTML =
    '<svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>'
  const iconPause = el('span', 'player__ctl-icon')
  iconPause.innerHTML =
    '<svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor">' +
    '<path d="M7 5h3v14H7zM14 5h3v14h-3z"/></svg>'
  iconPlay.style.display = 'none' // açılışda oynayır → «dayandır» ikonu görünür
  playToggle.appendChild(iconPlay)
  playToggle.appendChild(iconPause)
  controls.appendChild(playToggle)

  const times = el('div', 'player__times')
  const timeNow = el('span', 'player__time player__time--start', '0:00')
  const timeTotal = el('span', 'player__time player__time--end', '0:00')
  times.appendChild(timeNow)
  times.appendChild(el('span', 'player__time player__time--sep', '/'))
  times.appendChild(timeTotal)
  controls.appendChild(times)
  // Canlıda idarə sətri bütövlükdə mənasızdır: vaxt yoxdur (xətti yayım),
  // oynat/dayandır və seek isə bloklanıb (§5.13).
  if (options.live) controls.style.display = 'none'
  page.appendChild(controls)

  /**
   * Dayandırma nişanı — video pauzada olanda ekranın ortasında görünür.
   * ⚠ Chrome gizlənəndə DƏ görünür: pauza vəziyyəti istifadəçinin özünün
   *   qərarıdır və «niyə oynamır» sualı yaranmamalıdır.
   */
  const pausedBadge = el('div', 'player__paused', '❚❚')
  pausedBadge.style.display = 'none'
  page.appendChild(pausedBadge)

  /**
   * SARIMA GÖSTƏRİCİSİ — «+40 san» / «−10 san» (547).
   *
   * ⚠ Toplanan basışların NƏTİCƏSİNİ dərhal göstərir. Onsuz istifadəçi basır,
   *   ekranda heç nə dəyişmir (mövqe hələ yazılmayıb) və daha çox basır — sonra
   *   isə film gözlədiyindən qat-qat irəli tullanır. Saytda bunun qarşılığı
   *   scrubber-in sürüklənən başıdır; TV-də ox düymələrindən başqa yol yoxdur.
   * ⚠ Chrome gizli olsa DA görünür: sarıma istifadəçinin cari aksiyasıdır.
   */
  const seekBadge = el('div', 'player__seekbadge')
  seekBadge.style.display = 'none'
  page.appendChild(seekBadge)

  /**
   * BAĞLA (✕) — sol-üst küncdə (sahib tələbi 2026-08-04: «film playerində həm də
   * sol üst küncdə x iconu qoy ki vuranda bağlansın və film detal səhifəsinə
   * qayıtsın»).
   *
   * ⚠ TAM EKRAN DÜYMƏSİ ARTIQ YOXDUR (eyni tələb, 1-ci bənd): player həmişə tam
   *   ekranda açılır, ona görə «tam ekrana keç/çıx» seçimi mənasız idi — istifadəçi
   *   onu basıb pəncərə rejiminə düşəndə sol menyu videonun yanında qalırdı.
   *   Çıxış yolu indi TƏKDİR: ✕ / pultun Back-i / ikiqat klik → film səhifəsi.
   *
   * ⚠ SVG, simvol ŞRİFTİ DEYİL: şrift simvolları (✕ və s.) TV brauzerlərində ya
   *   yoxdur, ya da başqa ölçüdə çəkilir (bax `app.ts` → `svgIcon` səbəbi).
   */
  const closeButton = el('div', 'focusable player__close')
  closeButton.innerHTML =
    '<svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2.4" stroke-linecap="round"><path d="M5 5l14 14M19 5L5 19"/></svg>'
  if (options.live) closeButton.style.display = 'none'
  page.appendChild(closeButton)

  /**
   * ±10 saniyə düymələri (sahib tələbi 2026-08-04: «filmdə playerə 10 san önə
   * arxaya iconu qoy»).
   *
   * ⚠ EYNİ KOD YOLU pultun Sol/Sağ oxu ilədir (`seekBy`) — iki ayrı davranış
   *   yaranmır. Siçanla baxan istifadəçi üçün əvvəl sarımağın YOLU YOX İDİ.
   * ⚠ CANLIDA VƏ SEANSDA GİZLİDİR: orada seek qadağandır (§5.13) — görünən,
   *   amma işləməyən düymə «app sınıb» kimi oxunur.
   */
  const seekLock = Boolean(options.live || options.liveSession)

  /**
   * ⚠ QÖVS «10» RƏQƏMİNİN ÜSTÜNƏ DÜŞÜRDÜ (sahib bildirişi 2026-08-04: «saniyə ilə
   *   ox bir birinə girib normal hala sal»). ÖLÇÜLDÜ: köhnə qövsün radiusu 24-lük
   *   viewBox-da 5 vahid idi → 42px çəkilişdə ~9px, yəni daxili sahə ~17px; «10»
   *   yazısı isə 18px şriftdə ~20px enindədir — RƏQƏM QÖVSDƏN GENİŞ İDİ.
   *   İndi radius 8.5 (64px çəkilişdə ~45px diametr) — rəqəm rahat içəri sığır.
   * ⚠ SVG DƏ, RƏQƏM DƏ MÜTLƏQ MÖVQEDƏ və EYNİ 64px qutudadır: `vertical-align`
   *   ilə hizalama şrift metrikasından asılıdır və mərkəzləri 2-3px sürüşürdü
   *   (köhnə kodda `line-height: 67px` məhz bunu əl ilə düzəltməyə çalışırdı).
   */
  const arc =
    '<svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">'

  const back10 = el('div', 'focusable player__seek player__seek--back')
  back10.innerHTML =
    arc + '<path d="M12 3.5a8.5 8.5 0 1 1-8.5 8.5"/><path d="M12 3.5l2.6-2.6M12 3.5l2.6 2.6"/></svg>' +
    '<span class="player__seek-num">10</span>'

  const fwd10 = el('div', 'focusable player__seek player__seek--fwd')
  fwd10.innerHTML =
    arc + '<path d="M12 3.5a8.5 8.5 0 1 0 8.5 8.5"/><path d="M12 3.5l-2.6-2.6M12 3.5l-2.6 2.6"/></svg>' +
    '<span class="player__seek-num">10</span>'

  if (seekLock) {
    back10.style.display = 'none'
    fwd10.style.display = 'none'
  }
  controls.appendChild(back10)
  controls.appendChild(fwd10)

  /**
   * «NÖVBƏTİ BÖLÜM» — yalnız serialda və yalnız növbətisi VARSA.
   *
   * ⚠ Əvvəl keçid YALNIZ `ended` hadisəsində idi: bölümü yarımçıq qoyub
   *   növbətisinə keçmək üçün istifadəçi player-dən çıxıb detal ekranındakı
   *   siyahıya qayıtmalı idi (sahib bildirişi 2026-08-04: «player funksional
   *   olmalıdır, yarımçıq etmisən»).
   * ⚠ EYNİ KOD YOLU `ended` ilədir (`goToNextEpisode`) — iki davranış yaranmır.
   * ⚠ DÜYMƏ EKRANDA DEYİL, ⚙ PANELİNDƏDİR (2026-08-05, siçan qatı silinəndə):
   *   idarə sətrinin düymələri fokus almır — hər biri pultun ÖZ düyməsinə
   *   bağlıdır (⏯ · ±10 · YUXARI = panel · GERİ = çıxış). «Növbəti bölüm»ün isə
   *   pultda qarşılığı yoxdur, yəni siçan getdikdən sonra ekranda basıla bilməyən
   *   düymə kimi qalırdı. Panel sətri pultla gəzilir — funksiya itmir.
   */
  const isSeries = Boolean(
    options.contentId && options.episodeId && options.episodes && options.episodes.length > 0,
  )
  const upcoming =
    isSeries && options.episodes && options.episodeId
      ? nextEpisode(options.episodes, options.episodeId)
      : null
  /**
   * ƏVVƏLKİ BÖLÜM (547) — pultun «əvvəlki trek» düyməsi və ⚙ panelindəki bənd.
   * ⚠ Saytda bölüm siyahısı player-in ALTINDADIR və istifadəçi istənilən bölümə
   *   qayıda bilir; veb TV-də isə yalnız «növbəti» vardı, yəni geriyə yeganə yol
   *   player-dən çıxıb detala qayıtmaq idi.
   */
  const preceding =
    isSeries && options.episodes && options.episodeId
      ? previousEpisode(options.episodes, options.episodeId)
      : null

  /**
   * ⚙ PARAMETRLƏR — keyfiyyət · altyazı · səs yolu.
   *
   * ⚠ NİYƏ TƏK DÜYMƏ ALTINDA: idarə sətri onsuz da doludur (⏯ · vaxt · ±10 ·
   *   növbəti) və 10-foot maketdə hər əlavə ikon fokus yolunu uzadır.
   * ⚠ PANEL YALNIZ BU DÜYMƏ İLƏ AÇILIR — yuxarı ox 2026-08-06-da götürüldü
   *   (sahib: «üst oxu basan kimi quality açılır və bu yanlışdır»).
   * ⚠ CANLIDA GİZLİDİR: xətti yayımda rendition seçimi və altyazı yoxdur.
   */
  /**
   * İDARƏ SƏTRİNİN PULT FOKUSU (MYTasks 498 — «android tv edilən yanaşma veb
   * tv-də də olmalıdır»).
   *
   * ⚠⚠ NİYƏ LAZIM OLDU: keyfiyyət paneli ƏVVƏL yuxarı oxla açılırdı və sahib
   *   bunu «yanlışdır» adlandırdı (497). Yuxarı ox götürüləndə isə ⚙ düyməsi
   *   pultla ƏLÇATMAZ qaldı — bu ekranda oxlar birbaşa sarımaya, OK isə
   *   oynat/dayandırmaya bağlıdır, yəni sətir fokus modelində DEYİLDİ.
   *   Android TV-də bu problem yoxdur: orada düymələr Media3-ün öz idarə
   *   panelindədir və fokus təbii işləyir. Burada eyni davranış əl ilə qurulur.
   *
   * ⚠ MODEL Media3-ünkü ilə EYNİDİR: sətir SEÇİLİ DEYİLKƏN oxlar sarıyır
   *   (video idarəsi), AŞAĞI ox sətrə düşür, sağ/sol ikonlar arasında gəzir,
   *   OK seçir, YUXARI ox sətri tərk edir.
   * ⚠ `-1` = sətir seçilməyib. Bu vəziyyətdə heç bir davranış DƏYİŞMİR —
   *   köhnə istifadəçi vərdişi (ox = sarı, OK = oynat/dayandır) qorunur.
   */
  let controlIndex = -1

  /** Sətirdəki fokuslana bilən bəndlər — soldan sağa, gizlədilənlər atılır. */
  function controlItems(): Array<{ node: HTMLElement; run: () => void }> {
    const list: Array<{ node: HTMLElement; run: () => void }> = []
    if (playToggle.style.display !== 'none') list.push({ node: playToggle, run: togglePlayback })
    if (back10.style.display !== 'none') list.push({ node: back10, run: () => seekBy(-10) })
    if (fwd10.style.display !== 'none') list.push({ node: fwd10, run: () => seekBy(10) })
    if (menuButton.style.display !== 'none') list.push({ node: menuButton, run: openPanel })
    return list
  }

  function paintControlFocus(): void {
    const items = controlItems()
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item) setClass(item.node, 'is-focused', i === controlIndex)
    }
  }

  /** Sətri tərk edir — panel açılanda və ekran bağlananda da çağırılır. */
  function leaveControlRow(): void {
    controlIndex = -1
    paintControlFocus()
  }

  const menuButton = el('div', 'focusable player__ctl player__ctl--menu')
  menuButton.innerHTML =
    '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">' +
    '<circle cx="12" cy="12" r="3"/>' +
    '<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 ' +
    '1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 ' +
    '1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 ' +
    '1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 ' +
    '4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9' +
    'c.2.61.78 1 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>'
  if (options.live) menuButton.style.display = 'none'
  // ⚠ ⚙ İDARƏ SƏTRİNİN AXININDA DEYİL, EKRANIN ƏN SAĞINDADIR (sahib tələbi
  //   2026-08-08, MYTasks 569). `.player__controls` qabı SOL kənara bağlıdır
  //   (`left: 60`), yəni içindəki hər şey soldan sağa düzülür — ⚙ ±10
  //   düymələrinin dibində qalırdı. İndi düymə birbaşa `page`-in uşağıdır və
  //   `.player__ctl--menu` onu `right: 60` ilə zolağın sağ ucu ilə eyni oxa,
  //   `bottom: 46` ilə isə idarə sətri ilə eyni xəttə oturdur.
  // ⚠ FOKUS SIRASI DƏYİŞMİR: `controlItems()`-də ⚙ yenə SONUNCUDUR, yəni sağ ox
  //   ⟳10-dan ona keçir — indi hərəkət istiqaməti ekrandakı yerlə üst-üstə düşür.
  // ⚠ Qabdan çıxdığı üçün `opacity` mirası da itir — chrome gizlənməsi (5 san)
  //   `tv.css`-də ayrıca selektorla verilir, yoxsa düymə ekranda TƏK qalardı.
  page.appendChild(menuButton)

  /**
   * BUFERLƏMƏ GÖSTƏRİCİSİ.
   *
   * ⚠ REAL DEFEKT İDİ (2026-08-04 auditi): `waiting`/`stalled` hadisələri
   *   ÜMUMİYYƏTLƏ dinlənmirdi. TV-də bufer gözləməsi ekranda donmuş kadr kimi
   *   görünür və istifadəçi «app dondu» deyib pultu döyəcləyir — həmin basışlar
   *   isə növbəyə düşüb sonradan hamısı birdən icra olunur.
   * ⚠ CSS ANİMASİYASI `transform`-dur (layout doğurmur) və element yalnız
   *   göstəriləndə animasiya olunur — PERFORMANCE.md-nin daimi taymer qadağası
   *   pozulmur.
   */
  const spinner = el('div', 'player__spinner')
  spinner.style.display = 'none'
  page.appendChild(spinner)

  /** Parametrlər paneli — DOM açılanda qurulur (bax `openPanel`). */
  const panel = el('div', 'player__panel')
  panel.style.display = 'none'
  /**
   * Sətirlər AYRI QABDADIR, birbaşa panelin içində yox.
   *
   * ⚠ NİYƏ: panel `bottom`-dan yuxarı böyüyür və sətir sayı artdıqca (keyfiyyət +
   *   altyazı + səs + sürət) 1080 px-i keçir — ƏN YUXARIDAKI bölmə, yəni məhz
   *   «Keyfiyyət», kadrdan çıxırdı. İndi panelin hündürlüyü CSS-də məhduddur və
   *   bu qab `translate3d` ilə sürüşür (PERFORMANCE.md: `scrollTop` yox, transform).
   */
  const panelList = el('div', 'player__panel-list')
  panel.appendChild(panelList)
  page.appendChild(panel)

  // ⚠ Bu label YALNIZ YÜKLƏNMƏ vəziyyətini yazır («Video hazırlanır…»).
  //   Düymə izahı («Enter oynat/dayandır · ← → …») 2026-08-03-də SAHİB TƏLƏBİ ilə
  //   götürüldü. Yüklənmə mesajları qalır: onlarsız hazırlıq müddətində ekran boş
  //   qalırdı (§6 «boş/qara ekran yoxdur»).
  const hint = el('p', 'player__hint')
  page.appendChild(hint)

  // ⚠ Bütün state `let`-ləri if/else-dən ƏVVƏL elan olunur (TDZ-dən qorunmaq üçün).
  let hls: HlsLike | null = null
  let shakaPlayer: ShakaPlayerLike | null = null
  let mediaClosing: Promise<unknown> = Promise.resolve()
  let destroyed = false
  let notice: HTMLElement | null = null
  let liveTimer: number | null = null
  /** Canlı nəbz taymeri — `startLiveSync` ilə birlikdə qurulur, `destroy`-da təmizlənir. */
  let liveHeartbeatTimer: number | null = null
  /** Açıq `wch.playback_sessions` sətri — ekran söküləndə bitirilir. */
  let playbackSession: PlaybackSessionHandle | null = null
  let playbackOpening: Promise<PlaybackSessionHandle | null> | null = null
  /**
   * Biletin serverdə açdığı sessiya (`ticket.playbackSessionId`, 2026-10-25) — oynatma tərk
   * ediləndə `/sessions` iddiası olmasa da bitirilir (bax `releasePlaybackSession`).
   */
  let ticketSessionId: string | null = null
  /**
   * SESSİYA BAŞQA CİHAZA KEÇİB (409 `Playback.SessionReplaced`) — media sökülüb, oynadıcı
   * ölüdür: oynat/dayandır, sarıma və ⚙ paneli heç nə etmir. Çıxış yalnız «Yenidən cəhd et»
   * (yeni bilet) və «Geri»dir. İzah `onSessionReplaced`-dədir.
   */
  let replaced = false
  /**
   * Səhifə gizləndi/bağlandı (`visibilitychange` · `pagehide`) və sessiya bitirildi — oynatma
   * öz-özünə davam ETMİR; istifadəçi oynadanda `/sessions` ilə yeni sessiya açılır.
   */
  let suspended = false
  /**
   * Uzun pauza taymeri — pauza `PAUSED_SESSION_RELEASE_MS` fasiləsiz davam edəndə sessiya bitirilir
   * (2026-10-25, sahib qərarı 19; izah `lib/pausedSession.ts` və `releaseForLongPause`).
   */
  let pausedReleaseTimer: number | null = null
  let playbackAttempt = 0
  let livePollTimer: number | null = null
  let livePolling = false
  let liveProgramId = options.liveProgramId || ''
  const playbackOwner = readSession()
  const playbackOwnerGeneration = sessionGeneration()
  const playbackProfileId = readSelectedProfileId()
  const isViewerCurrent = (): boolean => {
    const current = readSession()
    return playbackOwnerGeneration === sessionGeneration() && playbackProfileId === readSelectedProfileId() &&
      (playbackOwner ? playbackOwner.userId : null) === (current ? current.userId : null)
  }
  let chromeTimer: number | null = null
  let chromeHidden = false
  /** Chrome sonuncu dəfə NƏ VAXT geri qayıtdı (bax `REVEAL_GRACE_MS`). */
  let chromeShownAt = 0
  /**
   * Bilet UĞURLA alındı — «Növbəti bölüm» yalnız bu halda mövcuddur (522;
   * Android `TvPlayerScreen`: `route.ticket == null → nextEpisode = null`,
   * 2026-08-05-də ölçülmüş qüsur: abunəsiz istifadəçi treylerdə «Növbəti bölüm»
   * görüb 403-lə izahsız qalırdı — panel bəndi və MEDIA_NEXT qısayolu da
   * bu bayraqdan asılıdır).
   */
  let ticketGranted = false
  /** Əl ilə bölüm keçidi yoldadır — təkrar basış ikinci bilet sorğusu açmasın. */
  let switchingEpisode = false
  /** Yuxarı lent bannerinin gizlənmə taymeri (bax `showBanner`). */
  let bannerTimer: number | null = null
  /**
   * ⚠ TAM EKRAN SAXLANILIR (547): bölümdən bölümə keçəndə ekran replace olunur,
   *   yəni köhnə player `destroy()` → `exitFullscreen()` edir, yenisi isə
   *   `enterFullscreen()`-i İSTİFADƏÇİ JESTİ OLMADAN çağırır və brauzer onu RƏDD
   *   EDİR (bax `lib/compat.ts`). Nəticədə serialı arxa-arxaya baxan istifadəçi
   *   ikinci bölümdə tam ekrandan düşürdü. Bayraq qalxanda tam ekrandan çıxılmır —
   *   yeni ekran onsuz da elə həmin rejimdə açılır.
   */
  let keepFullscreen = false
  /** Tam ekran dinləyicisinin götürülmə funksiyası (549 — bax `onFullscreenExit`). */
  let detachFullscreen: (() => void) | null = null
  /** Baxış mövqeyi taymeri (`PUT /api/me/watch-progress`, 15 san — 547). */
  let progressTimer: number | null = null
  /** Mövqe yazısı üçün profil — bilet alınanda saxlanılır (hər tikdə oxunmasın). */
  let progressProfileId: string | null = null
  /** Sonuncu yazılmış saniyə — eyni dəyər təkrar göndərilməsin. */
  let lastReportedSecond = -1
  /**
   * Bildiriş blokunun düymələri və fokus indeksi — izahı `showNotice`-un yanındadır.
   *
   * ⚠⚠ ELAN BURADADIR, BLOKUN YANINDA DEYİL (2026-08-13-də tapılmış REAL QÜSUR):
   *    `showNotice` mənbəsiz ekranda («nə film, nə treyler») MOUNT VAXTI, yəni
   *    aşağıdakı `let`-lərdən ƏVVƏL çağırılır — TDZ-yə görə
   *    «Cannot access 'noticeRetry' before initialization» atılırdı və EKRAN
   *    ÜMUMİYYƏTLƏ QURULMURDU (qara ekran, §6 pozuntusu). Faylın öz qaydası da
   *    budur: «Bütün state `let`-ləri if/else-dən ƏVVƏL elan olunur».
   */
  let noticeBack: HTMLElement | null = null
  let noticeRetry: HTMLElement | null = null
  let noticeIndex = 0

  /**
   * YUXARI LENT BANNERİ — treyler-fallback izahı və əl ilə bölüm keçidinin
   * xəta mesajı üçün (Android `TvPlayerScreen`-dəki TopCenter `Text` qarşılığı).
   *
   * ⚠ `showNotice` DEYİL: notice ekranın ortasında DAİMİ blokdur və «Enter =
   *   çıxış» qaydasını da işə salır — video OYNAYARKƏN bu, filmi/treyleri
   *   idarəedilməz edərdi. Banner isə kadrın üstündə kiçik lentdir və
   *   `BANNER_VISIBLE_MS`-dən sonra özü gizlənir (Android NOTICE_VISIBLE_MS).
   */
  const banner = el('div', 'player__banner')
  banner.style.display = 'none'
  page.appendChild(banner)

  function showBanner(text: string): void {
    if (destroyed) return
    setText(banner, text)
    banner.style.display = 'block'
    if (bannerTimer !== null) clearTimeout(bannerTimer)
    bannerTimer = window.setTimeout(() => {
      bannerTimer = null
      banner.style.display = 'none'
    }, BANNER_VISIBLE_MS)
  }

  if (options.live) {
    attachLive()
    livePollTimer = window.setTimeout(pollLiveProgram, 15000)
  } else if (options.contentId) {
    attachFullContent(options.contentId)
  } else if (options.trailerAsset) {
    // 2026-10-25: tam ekran treyleri də `trailerAllowed`-dən keçir (hero · modal · film səhifəsi
    //   düyməni onsuz da gizlədir — bu, ünvandan/köhnə marşrutdan gələn hal üçündür).
    if (trailerAllowed(options.ageLimit)) playTrailer(options.trailerAsset)
    else {
      const reason = reasonFor('age')
      showNotice(reason.title, reason.text)
    }
  } else {
    showNotice(T('Oynatma mümkün olmadı'), T('Bu ekran üçün nə film, nə treyler mənbəyi verilib.'))
  }

  // Ekran açılan kimi chrome görünür və 5 saniyəlik sayğac başlayır.
  showChrome()

  /**
   * ⚠⚠ 549 — «ESC BASANDA İLK DƏFƏDƏN DÖNÜŞ». Brauzer HƏQİQİ tam ekranda Esc-i
   *   ÖZÜ udur (tam ekranı bağlayır) və `keydown`-u səhifəyə vermir, yəni
   *   aşağıdakı `onKey` birinci basışı görmür — kök səbəbin tam izahı
   *   `lib/compat.ts → onFullscreenExit`-dədir. Tam ekranın BİZDƏN ASILI OLMADAN
   *   bağlanması «geri» basılışının özüdür, ona görə player-i bağlayır.
   *
   * ⚠ `destroyed` yoxlaması MƏCBURİDİR: `stopPlayback()` çıxarkən ÖZÜ
   *   `exitFullscreen()` çağırır və eyni hadisə yenidən gələrdi (ikiqat
   *   naviqasiya). Bayraq `stopPlayback`-in İLK sətrində qalxır.
   * ⚠ `keepFullscreen` (bölüm keçidi) halında hadisə onsuz da atılmır.
   * ⚠ Tam ekran ilk növbədə alınmayıbsa (jest şərti) hadisə heç vaxt gəlmir —
   *   davranış əvvəlki kimi qalır.
   */
  detachFullscreen = onFullscreenExit(() => {
    if (destroyed) return
    leavePlayer()
  })

  // ⚠ İŞARƏÇİ QATI YALNIZ BRAUZER VARİANTINDADIR — səbəb `bindPointer`-in
  //   başındadır. Cihaz bundle-ına bu kod ÜMUMİYYƏTLƏ düşmür (`IS_BROWSER`
  //   build vaxtı sabitdir, esbuild ölü budağı atır).
  if (IS_BROWSER) bindPointer()

  // ── Player chrome-unun avtomatik gizlənməsi ──────────────────────────────
  //
  // ⚠ SAHİB TƏLƏBİ (2026-08-03): «5 san sonra playerdəki timeline gizlənsin,
  //   daimi görünməməlidir». Timeline tək deyil, bütün alt chrome birlikdə
  //   gizlənir (qradiyent + film adı + zolaq + ipucu) — yalnız zolağı gizlətsək
  //   ekranın altında məzmunu örtən boş qradiyent və izah sətri qalardı.
  //
  // ⚠ CANLIDA TƏTBİQ OLUNMUR: orada zolaq və ipucu onsuz da `display:none`-dur,
  //   qalan chrome isə («● CANLI» nişanı, loqo, film adı) qəsdən DAİMİDİR —
  //   xətti yayımda kanalın nişanı gizlənməz.
  //
  // ⚠ `setInterval` YOX, tək `setTimeout`: hər düymə basılışında sayğac sıfırlanır,
  //   fon dövrü qalmır (PERFORMANCE.md — köhnə TV-də boş taymer thrash yaradır).
  //   Dəyişən yeganə xassə `opacity`-dir → kompozisiya, layout doğurmur.
  function showChrome(): void {
    if (options.live) return
    if (chromeHidden) {
      chromeHidden = false
      // ⚠ AN QEYD OLUNUR: işarəçi qatı «bu klik yalnız chrome-u gətirdi, yoxsa
      //   həqiqətən oynatmaya toxunmaq istəyir» sualını bununla ayırır
      //   (bax `REVEAL_GRACE_MS`).
      chromeShownAt = Date.now()
      setClass(page, 'is-chrome-hidden', false)
    }
    setClass(document.body, 'is-player-idle', false)
    if (chromeTimer !== null) clearTimeout(chromeTimer)
    chromeTimer = window.setTimeout(hideChrome, CHROME_IDLE_MS)
  }

  // ══════════════════════════════════════════════════════════════════════════
  // İŞARƏÇİ QATI (yalnız `browser` hədəfi — tv.kinobox.az)
  // ══════════════════════════════════════════════════════════════════════════
  //
  // ⚠⚠ SAHİB ŞİKAYƏTİ (2026-08-13): «veb tv playerlərində problem var, player
  //    açılır və controllar gizlənərkən ekran klikində yenidən görünmür … və
  //    playerin quality iconu da kliklənmir».
  //
  //    KÖK SƏBƏB: bu ekranda İŞARƏÇİ DİNLƏYİCİSİ ÜMUMİYYƏTLƏ YOX İDİ. Siçan
  //    qatı 2026-08-05-də qəsdən silinmişdi (bax `menuButton`-un yanındakı
  //    qeyd) — o vaxt yeganə hədəf PAKETLƏNMİŞ app idi və orada pultdan başqa
  //    giriş yoxdur. Brauzer variantı (MYTasks 693) isə məhz İŞARƏÇİ ilə idarə
  //    olunur: TV brauzeri ox düymələrini özü udur (`lib/platform.ts`), yəni
  //    ⚙ · ⏯ · ±10 · ✕ · zolaq — hamısı əlçatmaz idi.
  //
  // ⚠ CİHAZ SKİNİ TOXUNULMUR: `IS_BROWSER` build sabitidir, bütün bu blok
  //   `.wgt`/`.ipk` bundle-ından atılır (sıfır risk, sıfır bayt).
  // ⚠ PULT YOLU DƏYİŞMİR: hər klik EYNİ funksiyalara gedir (`togglePlayback`,
  //   `seekBy`, `openPanel`, `runPanel`, `leavePlayer`) — iki ayrı davranış
  //   yaranmır.
  function bindPointer(): void {
    // Hərəkət chrome-u geri gətirir (kursor `is-player-idle`-da gizlənir —
    // hərəkət olan kimi hər ikisi qayıdır).
    page.addEventListener('mousemove', () => {
      if (!destroyed) showChrome()
    }, false)

    /**
     * BOŞ SAHƏYƏ KLİK.
     *
     * ⚠⚠ «GİZLİ CHROME-U GERİ GƏTİRƏN» KLİK FİLMİ DAYANDIRMIR — şikayətin özü
     *    budur. Sadəcə `chromeHidden` yoxlaması KİFAYƏT ETMİR: siçan klikindən
     *    ƏVVƏL həmişə `mousemove` gəlir, o isə chrome-u onsuz da qaytarır. Yəni
     *    şərt kliki görəndə chrome ARTIQ görünəndir və film dayanardı
     *    (ölçüldü — brauzerdə `paused: true`). Ona görə qərar chrome-un NƏ VAXT
     *    qayıtdığına baxır: son 700 ms-də qayıdıbsa klik «göstər» deməkdir.
     * ⚠ Chrome ÇOXDANDIR görünürsə klik ADİ oynat/dayandırdır — masaüstü
     *   brauzerdə gözlənilən davranış (YouTube).
     * ⚠ İdarə düymələrinin öz `onclick`-i hadisəni SAXLAYIR (`clickable`), yəni
     *   bura yalnız kadrın özünə edilən klik gəlir.
     */
    page.addEventListener('click', () => {
      if (destroyed) return
      if (panelOpen) {
        closePanel()
        showChrome()
        return
      }
      const revealed = chromeHidden || Date.now() - chromeShownAt < REVEAL_GRACE_MS
      showChrome()
      if (revealed) return
      togglePlayback()
    }, false)

    /**
     * ZOLAĞA KLİK = HƏMİN NÖQTƏYƏ KEÇİD.
     *
     * ⚠ AYRICA «TUTMA ZOLAĞI» VAR: `.player__bar` cəmi 8px hündürlükdədir və
     *   siçanla ona dəymək praktiki deyil. Şəffaf 44px-lik qat zolağın üstündə
     *   oturur (`web.css` → `.player__barhit`), idarə sətrinə isə toxunmur.
     * ⚠ CANLIDA VƏ SEANSDA QURULMUR: orada seek qadağandır (§5.13) — işləməyən
     *   klik sahəsi «app sınıb» kimi oxunur.
     */
    if (!seekLock) {
      const hit = el('div', 'player__barhit')
      page.appendChild(hit)
      hit.onclick = (event: MouseEvent) => {
        event.stopPropagation()
        showChrome()
        seekToClientX(event.clientX)
      }
    }

    clickable(playToggle, togglePlayback)
    clickable(back10, () => seekBy(-10))
    clickable(fwd10, () => seekBy(10))
    clickable(menuButton, () => {
      if (panelOpen) closePanel()
      else openPanel()
    })
    clickable(closeButton, leavePlayer)
  }

  /**
   * Kliklənən idarə elementi.
   *
   * ⚠ `stopPropagation` MƏCBURİDİR: əks halda hadisə `page`-ə çatar və düymənin
   *   aksiyasından SONRA film də dayanardı (bir klik = iki hərəkət).
   * ⚠ `leaveControlRow()` pult yolundakı ilə eyni qaydadır: siçanla seçim
   *   ediləndə pultun fokus halqası ekranda «asılı» qalmamalıdır.
   */
  function clickable(node: HTMLElement, run: () => void): void {
    node.onclick = (event: MouseEvent) => {
      event.stopPropagation()
      if (destroyed) return
      showChrome()
      leaveControlRow()
      run()
    }
  }

  /** Zolaqdakı klik nöqtəsini vaxta çevirir və oraya keçir. */
  function seekToClientX(clientX: number): void {
    const duration = video.duration
    if (seekLock || replaced || !duration || isNaN(duration) || !isFinite(duration)) return
    // ⚠ `getBoundingClientRect` GÖRÜNƏN (transform tətbiq olunmuş) ölçünü verir,
    //   `clientX` isə pəncərə koordinatıdır — kino rejimindəki miqyas (`browser/
    //   shell.ts`) hesabı pozmur, çünki hər ikisi eyni fəzadadır.
    const box = bar.getBoundingClientRect()
    if (!box.width) return
    const ratio = Math.max(0, Math.min(1, (clientX - box.left) / box.width))
    const target = ratio * duration

    // Toplanan pult sarımasını ləğv edir — iki mənbə eyni anda `currentTime`
    // yazmamalıdır (bax `SEEK_COMMIT_MS`).
    if (seekTimer !== null) {
      clearTimeout(seekTimer)
      seekTimer = null
    }
    seekPending = false
    seekBadge.style.display = 'none'

    paintProgress(target)
    try {
      video.currentTime = target
    } catch {
      // Metadata gələnə qədər bəzi mənbələrdə seek atılır — zolaq növbəti
      // `timeupdate`-də həqiqi mövqeyə qayıdır.
    }
  }

  function hideChrome(): void {
    chromeTimer = null
    if (chromeHidden || destroyed) return
    chromeHidden = true
    // ⚠ SİNİF `page`-DƏ DEYİL, `body`-dədir (sahib tələbi 2026-08-03: «5 san sonra
    //   mouse cursor da itməlidir»). `cursor` xassəsi kursorun ALTINDAKI elementdən
    //   oxunur; player-in üstündə <video> və düymələr var və onların hər biri öz
    //   `cursor` dəyərini gətirir. `body`-dəki sinif isə bütün ağacı bir qaydada tutur.
    setClass(document.body, 'is-player-idle', true)
    setClass(page, 'is-chrome-hidden', true)
  }

  // ── Canlı xətti kanal — server saatına sinxron passiv baxış ──────────────
  // ══════════════════════════════════════════════════════════════════════════
  // BUFERLƏMƏ · PARAMETRLƏR PANELİ · NÖVBƏTİ BÖLÜM
  // ══════════════════════════════════════════════════════════════════════════

  /** Bufer gözləməsi — göstərici yalnız vəziyyət dəyişəndə yazılır. */
  let buffering = false
  function setBuffering(on: boolean): void {
    if (buffering === on || destroyed) return
    buffering = on
    spinner.style.display = on ? 'block' : 'none'
  }
  /**
   * Video ƏN AZI BİR KADR göstərdimi.
   * ⚠ NİYƏ SAXLANILIR: hls.js-in AÇILIŞDA sınması ilə OYNAYARKƏN sınması iki
   *   fərqli hadisədir — birincidə native oynadıcıya keçmək olar, ikincidə isə
   *   `video.src` yazmaq filmi başdan başladar (bax `playHlsUrl`).
   */
  let hasPlayed = false
  video.addEventListener('waiting', () => setBuffering(true), false)
  video.addEventListener('stalled', () => setBuffering(true), false)
  video.addEventListener(
    'playing',
    () => {
      hasPlayed = true
      setBuffering(false)
    },
    false,
  )
  video.addEventListener('canplay', () => setBuffering(false), false)
  // ⚠ `timeupdate` DƏ SÖNDÜRÜR: bəzi TV mühərriklərində `playing` bufer
  //   bərpasından sonra atılmır — göstərici ekranda ilişib qalardı.
  video.addEventListener('timeupdate', () => setBuffering(false), false)
  video.addEventListener('seeking', () => setBuffering(true), false)
  video.addEventListener('seeked', () => setBuffering(false), false)

  /**
   * ƏL İLƏ «Növbəti bölüm» (⚙ panel bəndi + pultun MEDIA_NEXT düyməsi).
   *
   * ⚠ BİLET NAVİQASİYADAN ƏVVƏL ALINIR (522; Android `advanceToNextEpisode`
   *   `manual = true`: «Əl ilə basılanda istifadəçi filmin ORTASINDADIR — bilet
   *   alınmasa cari bölüm də yoxa çıxırdı; F16 qaydası: qara ekran yox, izahlı
   *   mesaj»). Əvvəl ekran DƏRHAL replace olunurdu — yeni ekranda bilet
   *   alınmayanda istifadəçi baxdığı bölümü və mövqeyini itirirdi.
   * ⚠ Uğurda ekran yenə replace olunur və yeni ekran öz biletini istəyir
   *   (marşrut bilet daşımır) — bir ehtiyat sorğu; uğursuz halda isə CARİ
   *   OYNATMA DAVAM EDİR + səbəb yuxarıdakı lentdə (`showBanner`).
   * ⚠ AVTOMATİK (`ended`) yol OLDUĞU KİMİDİR — orada bölüm onsuz da bitib,
   *   birbaşa replace düzgündür (Android-dəki eyni ayrım).
   * ⚠ `ticketGranted` şərti: biletsiz (treyler/xəta) rejimdə keçid mövcud deyil
   *   (bayrağın elanındakı Android istinadı).
   */
  function goToNextEpisode(): void {
    if (upcoming) switchToEpisode(upcoming)
  }

  /**
   * İSTƏNİLƏN bölümə keçid (547) — «növbəti», «əvvəlki» və ⚙ panelindəki bölüm
   * siyahısı EYNİ yoldan keçir, yəni bilet yoxlaması, xəta mesajı və `replace`
   * qaydası bir yerdə qalır.
   */
  function switchToEpisode(target: Episode): void {
    const list = options.episodes
    if (!options.contentId || !list || !ticketGranted || switchingEpisode) return
    if (target.id === options.episodeId) return
    const contentId = options.contentId
    const profileId = readSelectedProfileId()
    if (!profileId) return

    // Cari mövqe İTMƏSİN: ekran replace olunmamışdan əvvəl son dəfə yazılır (547).
    flushProgress()

    switchingEpisode = true
    void requestTicket(contentId, profileId, target.id).then((result: PlaybackResult) => {
      switchingEpisode = false
      // 2026-10-25: yoxlama biletinin sessiyası — ekran bu arada söküldüsə oynatma olmayacaq.
      //   Uğurda bitirilmir: yeni ekranın bileti eyni sətri təkrar istifadə edir (server).
      if (destroyed || !isViewerCurrent()) {
        if (result.status === 'ok') endTicketSession(result.ticket)
        return
      }
      if (result.status !== 'ok') {
        showBanner(T('Növbəti bölüm açılmadı. Cari bölüm davam edir.'))
        return
      }
      // Tam ekrandan düşməmək üçün (bax `keepFullscreen` elanı).
      keepFullscreen = true
      navigate(
        {
          name: 'player',
          slug: options.slug,
          title: episodeTitle(options.title, target),
          contentId,
          episodeId: target.id,
          episodes: list,
          ageLimit: options.ageLimit,
          // 2026-10-25 (MYTasks 1118): bölüm keçidi EYNİ girişin davamıdır — hero-dan açılan
          //   serial növbəti bölümdə də çıxışda ana ekrana qayıtmalıdır. Əvvəl bayraq bu
          //   marşrutda itirdi və ikinci bölümdən çıxan istifadəçi modala düşürdü.
          fromHero: options.fromHero,
        },
        true,
      )
    })
  }

  interface PanelRow {
    node: HTMLElement
    run: (() => void) | null
  }

  let panelOpen = false
  let panelRows: PanelRow[] = []
  let panelIndex = 0
  /** «Avtomatik · 1080p» sətri — pillə dəyişəndə mətni yenilənir (F2). */
  let qualityAutoRow: HTMLElement | null = null

  /** Başlıq sətri — seçilmir, yalnız bölməni adlandırır. */
  function panelTitle(text: string): void {
    const node = el('div', 'player__panel-title', text)
    panelList.appendChild(node)
    panelRows.push({ node, run: null })
  }

  function panelOption(label: string, active: boolean, run: () => void): HTMLElement {
    const node = el('div', 'player__panel-row' + (active ? ' is-active' : ''), label)
    const index = panelRows.length
    panelList.appendChild(node)
    panelRows.push({ node, run })
    /*
      ⚠ SƏTİR SİÇANLA DA SEÇİLİR (brauzer variantı, 2026-08-13 sahib şikayəti:
        «playerin quality iconu da kliklənmir»). ⚙ düyməsini kliklə açmaq
        kifayət etmir — açılan siyahının SƏTİRLƏRİ də kliklənməlidir, yoxsa
        istifadəçi keyfiyyəti görür, amma seçə bilmir.
      ⚠ `runPanel()` çağırılır, `run()` DEYİL: seçimdən sonra panelin bağlanması
        və chrome sayğacının sıfırlanması pult yolunda da elə oradadır.
    */
    if (IS_BROWSER) {
      node.onclick = (event: MouseEvent) => {
        event.stopPropagation()
        if (destroyed) return
        panelIndex = index
        paintPanel()
        runPanel()
      }
    }
    return node
  }

  /** «1080p» / «720p» — hündürlük yoxdursa bitrate. */
  /**
   * ⚠ AD PİKSEL HÜNDÜRLÜYÜ DEYİL, 16:9 EKVİVALENT PİLLƏSİDİR (sahib bildirişi
   *   2026-08-05: «Fəlakət filminin keyfiyyəti max 812p yazır niyə 1080p deyil»).
   *   Ölçüldü — həmin filmin ən yuxarı variantı 1920×814-dür (2.36:1 sinema
   *   nisbəti, yəni TAM HD). Etiket hündürlükdən götürüləndə «814p» çıxırdı.
   *   Hesab `packages/player/src/qualityLabel.ts` ilə EYNİDİR (altı səthdə eyni ad).
   * ⚠ TV app-ı `@kinobox/player`-i YALNIZ DRM yolunda import edir; bu funksiya isə
   *   native/hls.js yollarında da lazımdır, ona görə hesab burada təkrarlanır.
   *   Dəyişəndə HƏR İKİ yer yenilənməlidir (bax həmin faylın başlığı).
   */
  const QUALITY_TIERS = [2160, 1440, 1080, 720, 540, 480, 360, 270, 240, 144]

  function qualityTier(width: number | null | undefined, height: number): number {
    const nominal = width && width > 0 ? Math.max(height, Math.round((width * 9) / 16)) : height
    if (nominal <= 0) return 0
    for (let i = 0; i < QUALITY_TIERS.length; i++) {
      const tier = QUALITY_TIERS[i] as number
      if (Math.abs(nominal - tier) <= tier * 0.12) return tier
    }
    return nominal
  }

  function qualityLabel(
    width: number | null | undefined,
    height: number | null | undefined,
    bitrate: number | null | undefined,
  ): string {
    if (height) return qualityTier(width, height) + 'p'
    if (bitrate) return Math.round(bitrate / 1000) + ' kbit/s'
    return T('Naməlum')
  }

  /**
   * HAZIRDA OYNAYAN pillənin hündürlüyü — F2 («cari pillə görünür»).
   *
   * ⚠ MƏNBƏ SIRASI: mühərrikin özü (hls.js `levels[currentLevel]` · Shaka aktiv
   *   variant) → sonra dekoderin faktiki ölçüsü (`video.videoHeight`).
   *   Sonuncu KINOBOX_PLAYER.md §5-in tələb etdiyi ÖLÇÜLMÜŞ dəyərdir və manifestdə
   *   `RESOLUTION` yazılmayan mənbələrdə (bəzi Vimeo həlləri) yeganə həqiqətdir.
   * ⚠ Siyahı MANİFESTDƏN gəlir, sabit yazılmır (F1).
   */
  function activeFrame(): { width: number; height: number } | null {
    if (hls && hls.levels) {
      const index = typeof hls.currentLevel === 'number' ? hls.currentLevel : -1
      const level = index >= 0 ? (hls.levels[index] as { width?: number; height?: number }) : null
      if (level && level.height) return { width: level.width || 0, height: level.height }
    } else if (shakaPlayer && shakaPlayer.getVariantTracks) {
      const tracks = shakaPlayer.getVariantTracks()
      if (tracks) {
        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i] as ShakaTrack
          if (track && track.active && track.height) {
            return { width: track.width || 0, height: track.height }
          }
        }
      }
    }
    // Son mənbə DEKODERİN ÖZÜDÜR — manifestdə `RESOLUTION` olmayan mənbədə yeganə həqiqət.
    if (video.videoHeight > 0) return { width: video.videoWidth || 0, height: video.videoHeight }
    return null
  }

  /**
   * «Avtomatik · 1080p» (F2).
   *
   * ⚠ NİYƏ LAZIMDIR: paneldə yalnız «Avtomatik» yazılırdı və istifadəçi NƏYƏ
   *   BAXDIĞINI bilmirdi. Sahibin «heç birində video keyfiyyətli açmır» şikayətini
   *   istifadəçinin öz gözü ilə yoxlamasının YEGANƏ yolu budur.
   * ⚠ Hündürlük hələ məlum deyilsə (manifest təzəcə açılıb) sadəcə «Avtomatik»
   *   qalır — «Avtomatik · Naməlum» yazmaq məlumat vermir, narahatlıq yaradır.
   */
  function autoQualityLabel(): string {
    const frame = activeFrame()
    return frame ? T('Avtomatik') + ' · ' + qualityTier(frame.width, frame.height) + 'p' : T('Avtomatik')
  }

  /**
   * ABR pilləni dəyişəndə paneldəki yazını yeniləyir.
   * ⚠ YALNIZ PANEL AÇIQ OLANDA bir `textContent` yazısı — bağlı paneldə heç nə
   *   edilmir (PERFORMANCE.md: görünməyən elementə iş görmək kadr yeyir).
   */
  function refreshQualityLabel(): void {
    if (!panelOpen || destroyed || !qualityAutoRow) return
    setText(qualityAutoRow, autoQualityLabel())
  }

  /**
   * Oynatma sürətləri (F5). `video.playbackRate` HƏR ÜÇ MÜHƏRRİKDƏ eynidir
   * (native · hls.js · Shaka) — üçü də eyni `<video>` elementini oynadır.
   */
  const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2]

  /**
   * Panelin məzmununu QURUR.
   *
   * ⚠ DOM BURADA YARADILIR — «DOM bir dəfə qurulur» qaydasının (PERFORMANCE.md)
   *   pozulması DEYİL: qayda FOKUS və SÜRÜŞMƏ hot-path-ı üçündür. Panel yalnız
   *   istifadəçi düyməni basanda açılır, sətir sayı 3-12-dir və oynatma dayanmır.
   * ⚠ MƏNBƏSİ OLMAYAN BÖLMƏ ÜMUMİYYƏTLƏ GÖSTƏRİLMİR: boş «Altyazı» başlığı
   *   «app sınıb» kimi oxunur. Ona görə hər bölmə əvvəlcə yoxlanılır.
   */
  function buildPanel(): void {
    // ⚠ `innerHTML = ''` DEYİL: boş sətri belə HTML parseri işə salır. `clear()`
    //   uşaqları düyün-düyün götürür (köhnə TV-də ölçülə bilən fərq).
    clear(panelList)
    panelRows = []
    qualityAutoRow = null

    // ── Keyfiyyət (F1) + cari pillə (F2) ───────────────────────────────────
    const levels = hls && hls.levels ? hls.levels : null
    if (levels && levels.length > 1) {
      panelTitle(T('Keyfiyyət'))
      /**
       * ⚠ `currentLevel < 0` «AVTOMATİK» DEMƏK DEYİL — köhnə kodun səhvi bu idi.
       *   hls.js-də `currentLevel` OXUNANDA hazırda oynayan pilləni verir (ABR
       *   açıq olsa belə ≥ 0), YAZILANDA isə −1 ABR-ı geri qaytarır. Nəticədə
       *   avtomatik rejimdə «Avtomatik» sətri heç vaxt nişanlanmır, əvəzinə
       *   konkret pillə «seçilmiş» görünürdü — yəni istifadəçi ABR-ın işlədiyini
       *   bilmirdi. Rejimi `autoLevelEnabled` deyir; kitabxananın köhnə nüsxəsində
       *   o yoxdursa öz bayrağımıza (`autoQuality`) düşürük.
       */
      const auto = hls && typeof hls.autoLevelEnabled === 'boolean' ? hls.autoLevelEnabled : autoQuality
      const current = hls && typeof hls.currentLevel === 'number' ? hls.currentLevel : -1
      qualityAutoRow = panelOption(autoQualityLabel(), auto, () => {
        autoQuality = true
        if (hls) hls.currentLevel = -1
      })
      // Yüksəkdən aşağıya — istifadəçi adətən ən yaxşısını axtarır.
      const order: number[] = []
      for (let i = 0; i < levels.length; i++) order.push(i)
      order.sort((a, b) => levelHeight(levels[b]) - levelHeight(levels[a]))
      for (let i = 0; i < order.length; i++) {
        const index = order[i] as number
        const level = levels[index] as { width?: number; height?: number; bitrate?: number }
        panelOption(qualityLabel(level.width, level.height, level.bitrate), !auto && current === index, () => {
          // Əl ilə seçim ABR-ı söndürür (KINOBOX_PLAYER.md K5).
          autoQuality = false
          if (hls) hls.currentLevel = index
        })
      }
    } else if (shakaPlayer && shakaPlayer.getVariantTracks && shakaPlayer.selectVariantTrack) {
      const tracks = shakaPlayer.getVariantTracks()
      if (tracks && tracks.length > 1) {
        panelTitle(T('Keyfiyyət'))
        // ⚠ Shaka-da «avtomatik» ayrıca yol deyil — ABR bayrağıdır.
        qualityAutoRow = panelOption(autoQualityLabel(), autoQuality, () => {
          autoQuality = true
          if (shakaPlayer) shakaPlayer.configure({ abr: { enabled: true } })
        })
        const sorted = tracks.slice(0).sort((a, b) => (b.height || 0) - (a.height || 0))
        for (let i = 0; i < sorted.length; i++) {
          const track = sorted[i] as ShakaTrack
          panelOption(qualityLabel(track.width, track.height, track.bandwidth), !autoQuality && track.active, () => {
            autoQuality = false
            if (shakaPlayer) shakaPlayer.configure({ abr: { enabled: false } })
            if (shakaPlayer && shakaPlayer.selectVariantTrack) shakaPlayer.selectVariantTrack(track, true)
          })
        }
      }
    }

    /**
     * SƏS/ALTYAZI YOLUNUN ETİKETİ — XAM BCP-47 KODU EKRANA ÇIXMIR (2026-08-30).
     *
     * ⚠ NƏ OLURDU: manifestdə ad (`name`/`label`) yoxdursa menyuda MƏHZ kod
     *   görünürdü — «az», «ru», «tr». Üç metrdən bu, seçim deyil, şifrədir;
     *   saytda və Android TV-də isə dilin öz adı yazılır. `languageNative()`
     *   («Azərbaycan» · «Русский» · «Türkçe» · «English») onsuz da mövcuddur —
     *   dil çipləri və Profil sətri həmin siyahıdan gəlir, yəni ad İKİ YERDƏ
     *   fərqli yazıla bilmir.
     * ⚠ TANINMAYAN KOD BÖYÜK HƏRFLƏ QAYIDIR («fr» → «FR») — mətn İTMİR, sadəcə
     *   tərcüməsi yoxdur (lüğətdəki eyni «açar tapılmasa azərbaycanca» qaydası).
     */
    const trackLanguage = (code: string | undefined): string => (code ? languageNative(code) : '')

    // ── Altyazı ────────────────────────────────────────────────────────────
    const subs = hls && hls.subtitleTracks ? hls.subtitleTracks : null
    if (subs && subs.length > 0) {
      panelTitle(T('Altyazı'))
      const active = hls && typeof hls.subtitleTrack === 'number' ? hls.subtitleTrack : -1
      panelOption(T('Söndür'), active < 0, () => {
        if (hls) hls.subtitleTrack = -1
      })
      for (let i = 0; i < subs.length; i++) {
        const track = subs[i] as { id?: number; name?: string; lang?: string }
        const id = typeof track.id === 'number' ? track.id : i
        panelOption(track.name || trackLanguage(track.lang) || T('Altyazı'), active === id, () => {
          if (hls) hls.subtitleTrack = id
        })
      }
    } else if (shakaPlayer && shakaPlayer.getTextTracks && shakaPlayer.selectTextTrack) {
      const texts = shakaPlayer.getTextTracks()
      if (texts && texts.length > 0) {
        panelTitle(T('Altyazı'))
        const visible = shakaPlayer.isTextTrackVisible ? shakaPlayer.isTextTrackVisible() : false
        panelOption(T('Söndür'), !visible, () => {
          if (shakaPlayer && shakaPlayer.setTextTrackVisibility) shakaPlayer.setTextTrackVisibility(false)
        })
        for (let i = 0; i < texts.length; i++) {
          const track = texts[i] as ShakaTrack
          panelOption(track.label || trackLanguage(track.language) || T('Altyazı'), visible && track.active, () => {
            if (shakaPlayer && shakaPlayer.selectTextTrack) shakaPlayer.selectTextTrack(track)
            if (shakaPlayer && shakaPlayer.setTextTrackVisibility) shakaPlayer.setTextTrackVisibility(true)
          })
        }
      }
    }

    // ── Səs yolu ───────────────────────────────────────────────────────────
    const audios = hls && hls.audioTracks ? hls.audioTracks : null
    if (audios && audios.length > 1) {
      panelTitle(T('Səs'))
      const active = hls && typeof hls.audioTrack === 'number' ? hls.audioTrack : 0
      for (let i = 0; i < audios.length; i++) {
        const track = audios[i] as { id?: number; name?: string; lang?: string }
        const id = typeof track.id === 'number' ? track.id : i
        panelOption(track.name || trackLanguage(track.lang) || T('Səs'), active === id, () => {
          if (hls) hls.audioTrack = id
        })
      }
    } else if (shakaPlayer && shakaPlayer.getAudioLanguages && shakaPlayer.selectAudioLanguage) {
      /**
       * ⚠ DRM YOLUNDA SƏS SEÇİMİ YOX İDİ (547 auditi): bölmə yalnız hls.js
       *   budağında qurulurdu, yəni məhz QORUNAN filmlərdə — çoxdilli səsi olan
       *   yeganə kataloq hissəsində — menyu «səs» bəndini ümumiyyətlə göstərmirdi.
       *   Hər iki etalonda seçim var (saytda Mux-un audio menyusu, Android TV-də
       *   Media3-ün `SETTINGS_AUDIO_TRACK_SELECTION`).
       * ⚠ Shaka-da səs DİL SƏVİYYƏSİNDƏ seçilir (`selectAudioLanguage`), variant
       *   səviyyəsində yox: variantı kilidləsək ABR həmin dilin pillələri arasında
       *   da adaptasiya edə bilməzdi (keyfiyyət donardı).
       */
      const languages = shakaPlayer.getAudioLanguages()
      if (languages && languages.length > 1) {
        panelTitle(T('Səs'))
        const variants = shakaPlayer.getVariantTracks ? shakaPlayer.getVariantTracks() : null
        let activeLanguage = ''
        if (variants) {
          for (let i = 0; i < variants.length; i++) {
            const track = variants[i] as ShakaTrack
            if (track && track.active && track.language) activeLanguage = track.language
          }
        }
        for (let i = 0; i < languages.length; i++) {
          const language = languages[i] as string
          panelOption(languageNative(language), activeLanguage === language, () => {
            if (shakaPlayer && shakaPlayer.selectAudioLanguage) shakaPlayer.selectAudioLanguage(language)
          })
        }
      }
    }

    /**
     * ── Sürət (F5) ────────────────────────────────────────────────────────
     *
     * ⚠ VEB TV-DƏ ÜMUMİYYƏTLƏ YOX İDİ (KINOBOX_PLAYER.md §4 cədvəli: `✗`),
     *   halbuki bənd bütün səthlərdə tələb olunur.
     * ⚠ MƏNBƏDƏN ASILI DEYİL, ona görə mühərrik yoxlanılmır: `video.playbackRate`
     *   native, hls.js və Shaka yollarının HAMISINDA eyni `<video>` elementinə
     *   düşür.
     * ⚠ SEEK KİLİDLİ REJİMDƏ GİZLİDİR («canlı» + «birlikdə bax» seansı): orada
     *   hamı eyni kadrı görməlidir (§5.13) — sürət dəyişsə baxanlar bir-birindən
     *   ayrılar. Xətti canlıda panel onsuz da açılmır.
     */
    if (!seekLock) {
      panelTitle(T('Sürət'))
      const rate = video.playbackRate > 0 ? video.playbackRate : 1
      for (let i = 0; i < SPEEDS.length; i++) {
        const speed = SPEEDS[i] as number
        // Kəsr müqayisəsi TOLERANSLIDIR: 0.75 kimi dəyərlər ikilik surətdə dəqiq deyil.
        panelOption(speed + '×', Math.abs(rate - speed) < 0.01, () => {
          try {
            video.playbackRate = speed
          } catch {
            // ⚠ Bəzi TV mühərrikləri 1×-dən fərqli sürəti rədd edir və istisna atır.
            //   Uduzsaq panel növbəti açılışda yenə 1×-i nişanlayır — yalan
            //   göstərici qalmır, app isə çökmür.
          }
        })
      }
    }

    // ── Bölüm (serial) ──────────────────────────────────────────────────────
    // ⚠ Ən sonda: sürət/altyazı gündəlik seçimdir, bölüm keçidi isə ekranı
    //   DƏYİŞİR — təsadüfən basılsa istifadəçi izlədiyi bölümü itirir.
    // ⚠ YALNIZ BİLET ALINIBSA (522): biletsiz (treyler/xəta) rejimdə «Növbəti
    //   bölüm» menyudan ÇIXIR — Android `TvPlayerScreen`-də 2026-08-05-də
    //   ölçülmüş qüsur: abunəsiz istifadəçi treylerdə bəndi görüb hər basışda
    //   403-lə izahsız uğursuz ekrana düşürdü.
    /**
     * ⚠ ARTIQ TAM SİYAHIDIR, tək «Növbəti bölüm» bəndi DEYİL (547 — sayt
     *   pariteti: `WatchClient.tsx` player-in altında `EpisodeList` göstərir və
     *   istifadəçi İSTƏNİLƏN bölümə keçə bilir). Veb TV-də isə yeganə yol irəli
     *   idi; geri qayıtmaq üçün player-dən çıxmaq lazım gəlirdi.
     * ⚠ VİDEOSUZ BÖLÜM SİYAHIYA DÜŞMÜR: basılanda 403 alıb izahsız qalardı
     *   (`hasVideo === false` sətri saytda da klik almır).
     * ⚠ Cari bölüm nişanlanır (`is-active`) və seçilə bilər, amma `switchToEpisode`
     *   onu erkən kəsir — ekranı özünə replace etmək filmi başdan başladardı.
     */
    if (isSeries && !options.live && ticketGranted && options.episodes) {
      const list = options.episodes
      const playable: Episode[] = []
      for (let i = 0; i < list.length; i++) {
        const episode = list[i] as Episode
        if (episode.hasVideo) playable.push(episode)
      }
      if (playable.length > 1) {
        panelTitle(T('Bölüm'))
        for (let i = 0; i < playable.length; i++) {
          const episode = playable[i] as Episode
          const label =
            episode.number + T('-ci bölüm') + (episode.title ? ' · ' + episode.title : '')
          panelOption(label, episode.id === options.episodeId, () => {
            switchToEpisode(episode)
          })
        }
      }
    }

    // Heç bir bölmə yoxdursa istifadəçi səbəbi görməlidir (boş panel yox).
    if (panelRows.length === 0) {
      panelTitle(T('Bu videoda əlavə seçim yoxdur'))
    }
  }

  /** Shaka-da ABR açıqdırmı — «Avtomatik» sətrinin nişanı üçün. */
  let autoQuality = true

  function paintPanel(): void {
    for (let i = 0; i < panelRows.length; i++) {
      const row = panelRows[i] as PanelRow
      setClass(row.node, 'is-focused', i === panelIndex)
    }
  }

  function firstSelectable(from: number, step: number): number {
    for (let i = from; i >= 0 && i < panelRows.length; i += step) {
      if ((panelRows[i] as PanelRow).run) return i
    }
    return panelIndex
  }

  /**
   * ── PANELİN SÜRÜŞMƏSİ ────────────────────────────────────────────────────
   *
   * ⚠ NİYƏ LAZIM OLDU (2026-08-05): panel `bottom`-dan YUXARI böyüyür. Sürət
   *   bölməsi əlavə olunandan sonra dolu panel — keyfiyyət (1 başlıq + 1 avtomatik
   *   + 4 pillə) + altyazı (1 + 1 + N) + səs (1 + N) + sürət (1 + 6) — 20-dən çox
   *   sətir edir. 1080 px-lik ekranda bu, ƏN YUXARIDAKI bölmənin, yəni məhz
   *   «Keyfiyyət»in kadrdan çıxması deməkdir: F1 və F2 GÖRÜNMƏZ olardı.
   *
   * ⚠ ÖLÇÜ YALNIZ PANEL AÇILANDA OXUNUR, fokus addımında YOX. PERFORMANCE.md §3
   *   qadağası hər addımda `getBoundingClientRect`/`offsetTop` oxumağa aiddir
   *   (60 kartlıq ekranda ox basışı = 60 layout). Burada ölçülər bir dəfə massivə
   *   yığılır — `main.ts → scaleViewport()` ilə eyni naxış — sonrakı hərəkət saf
   *   arifmetikadır. Panel həm də istifadəçinin qəsdən açdığı, nadir hadisədir.
   *
   * ⚠ `scrollTop` DEYİL, `translate3d` (PERFORMANCE.md §3): köhnə TV-də sürüşmə
   *   yalnız compositor qatında ucuzdur.
   */
  let panelTops: number[] = []
  let panelBottoms: number[] = []
  /** Panelin görünən hündürlüyü (CSS `max-height`) — açılışda bir dəfə oxunur. */
  let panelViewport = 0
  let panelShift = 0

  function measurePanel(): void {
    panelTops = []
    panelBottoms = []
    panelShift = 0
    translate(panelList, 0, 0)
    // ⚠ Bu üç sətir yeganə layout oxunuşudur və panel EKRANDA görünəndən sonra
    //   çağırılmalıdır: `display: none` olan elementin `offsetTop`-u 0-dır.
    panelViewport = panel.clientHeight
    for (let i = 0; i < panelRows.length; i++) {
      const node = (panelRows[i] as PanelRow).node
      panelTops.push(node.offsetTop)
      panelBottoms.push(node.offsetTop + node.offsetHeight)
    }
  }

  /** Fokuslanan sətri görünən pəncərəyə gətirir (yalnız lazım olanda). */
  function scrollPanel(): void {
    if (panelViewport <= 0) return
    const top = panelTops[panelIndex]
    const bottom = panelBottoms[panelIndex]
    if (typeof top !== 'number' || typeof bottom !== 'number') return
    if (top - panelShift < 0) panelShift = top
    else if (bottom - panelShift > panelViewport) panelShift = bottom - panelViewport
    else return
    translate(panelList, 0, -panelShift)
  }

  function openPanel(): void {
    if (panelOpen || options.live || destroyed || replaced) return
    buildPanel()
    panelOpen = true
    panel.style.display = 'block'
    panelIndex = firstSelectable(0, 1)
    paintPanel()
    measurePanel()
    scrollPanel()
    showChrome()
  }

  function closePanel(): void {
    if (!panelOpen) return
    panelOpen = false
    panel.style.display = 'none'
  }

  function movePanel(step: number): void {
    const next = firstSelectable(panelIndex + step, step)
    if (next === panelIndex) return
    panelIndex = next
    paintPanel()
    scrollPanel()
  }

  /** Seçimi tətbiq edir və paneli bağlayır (seçimdən sonra panel yerində qalmır). */
  function runPanel(): void {
    const row = panelRows[panelIndex]
    if (row && row.run) row.run()
    closePanel()
    showChrome()
  }

  function attachLive(): void {
    const asked = ++playbackAttempt
    bar.style.display = 'none' // passiv baxış: scrubber göstərilmir
    hint.style.display = 'none' // canlıda izahlı hint yoxdur (chrome: badge/loqo/film adı)
    setText(hint, '')

    const profileId = readSelectedProfileId()
    if (!profileId) {
      showNotice(T('Baxış profili seçilməyib'), T('Profil bölməsindən profil seçin.'))
      return
    }

    void requestLivePlayback(profileId).then((result: LivePlaybackResult) => {
      if (destroyed || asked !== playbackAttempt || !isViewerCurrent()) return
      if (result.status === 'ok') {
        startFullPlayback(result.ticket)
        startLiveSync(
          typeof result.ticket.startPositionSeconds === 'number' ? result.ticket.startPositionSeconds : 0,
        )
        startLiveHeartbeat(profileId)
        return
      }
      if (result.status === 'country') {
        const reason = reasonFor('country')
        showNotice(reason.title, reason.text)
      } else if (result.status === 'age') {
        const reason = reasonFor('age')
        showNotice(reason.title, reason.text)
      } else if (result.status === 'forbidden') {
        showNotice(T('Abunəlik lazımdır'), T('Canlı yayıma baxış üçün abunə olun (telefonda və ya saytda).'))
      } else if (result.status === 'unauthorized') {
        // ⚠ BAŞLIQ DA TƏRCÜMƏ OLUNUR (2026-08-30): gövdə sarınmışdı, başlıq isə
        //   çılpaq qalmışdı — bildiriş yarısı ingiliscə, yarısı azərbaycanca çıxırdı.
        showNotice(T('Daxil olun'), T('İzləmək üçün Profil bölməsindən QR ilə daxil olun.'))
      } else if (result.status === 'noBroadcast') {
        showNotice(T('Yayım yoxdur'), T('Hazırda cədvəldə proqram yoxdur.'))
      } else {
        // Şəbəkə/hazırlıq problemi — təkrar cəhd mənalıdır (547).
        showNotice(T('Oynatma hazır deyil'), T('Canlı yayım hazırlana bilmədi.'), true)
      }
    })
  }

  function clearLiveLoops(): void {
    if (liveTimer !== null) window.clearTimeout(liveTimer)
    if (liveHeartbeatTimer !== null) window.clearTimeout(liveHeartbeatTimer)
    liveTimer = null
    liveHeartbeatTimer = null
  }

  function pollLiveProgram(): void {
    if (livePollTimer !== null) window.clearTimeout(livePollTimer)
    livePollTimer = null
    if (destroyed || !isViewerCurrent() || livePolling) return
    livePolling = true
    void getLiveNow().then((now) => {
      livePolling = false
      if (destroyed || !isViewerCurrent()) return
      // Şəbəkə xətası (null) mövcud yayımı kəsmir; serverin açıq boşluq cavabı kəsir.
      if (now) {
        const current = now.isActive ? now.current : null
        if (!current) {
          if (liveProgramId !== 'gap') {
            liveProgramId = 'gap'
            playbackAttempt += 1
            clearLiveLoops()
            resetMedia()
            showNotice(T('Yayım yoxdur'), T('Hazırda cədvəldə proqram yoxdur.'))
          }
        } else {
          const changed = liveProgramId !== '' && liveProgramId !== current.id
          liveProgramId = current.id
          const name = page.querySelector('.player__live-name')
          if (name) setText(name as HTMLElement, current.title)
          if (changed) retryPlayback()
        }
      }
      livePollTimer = window.setTimeout(pollLiveProgram, 15000)
    })
  }

  /**
   * Playhead-i canlı kənara pin edir: gözlənilən mövqe = anchor + keçən real vaxt.
   * setInterval YOX — özünü yenidən planlayan setTimeout (qr poll kimi), köhnə TV-də thrash olmasın.
   */
  function startLiveSync(anchorSeconds: number): void {
    if (liveTimer !== null) window.clearTimeout(liveTimer)
    const asked = playbackAttempt
    const anchorWall = Date.now()
    const tick = (): void => {
      if (destroyed || asked !== playbackAttempt || !isViewerCurrent()) return
      const target = anchorSeconds + (Date.now() - anchorWall) / 1000
      if (video.duration && isFinite(video.currentTime) && Math.abs(video.currentTime - target) > 4) {
        try {
          video.currentTime = target
        } catch {
          // buffering — növbəti tikdə düzələcək
        }
      }
      if (video.paused) void video.play().catch(() => undefined)
      liveTimer = window.setTimeout(tick, 2500)
    }
    liveTimer = window.setTimeout(tick, 2500)
  }

  /**
   * Canlı nəbz: izləyici sayğacı üçün hər 15 s `POST /api/live/heartbeat` (bax lib/live.ts).
   * `startLiveSync` kimi özünü yenidən planlayan setTimeout — köhnə TV-də interval yığını olmasın.
   * Yalnız video oynayanda göndərilir (pauza/xəta halında say şişməsin).
   */
  function startLiveHeartbeat(profileId: string): void {
    if (liveHeartbeatTimer !== null) window.clearTimeout(liveHeartbeatTimer)
    const asked = playbackAttempt
    const tick = (): void => {
      if (destroyed || asked !== playbackAttempt || !isViewerCurrent()) return
      if (!video.paused) void sendLiveHeartbeat(profileId)
      liveHeartbeatTimer = window.setTimeout(tick, LIVE_HEARTBEAT_MS)
    }
    void sendLiveHeartbeat(profileId)
    liveHeartbeatTimer = window.setTimeout(tick, LIVE_HEARTBEAT_MS)
  }

  // ── Tam film rejimi — bilet + oynatma ────────────────────────────────────
  function attachFullContent(contentId: string): void {
    const asked = ++playbackAttempt
    setText(hint, options.liveSession ? T('Canlı seans hazırlanır…') : T('Video hazırlanır…'))

    const profileId = readSelectedProfileId()
    if (!profileId) {
      degradeToTrailer(T('Baxış profili seçilməyib'), T('Profil bölməsindən profil seçin.'))
      return
    }

    const beginGrantedPlayback = (ticket: PlaybackTicket): void => {
      // «Növbəti bölüm» yalnız bilet alınandan sonra mövcud olur (bax bayrağın
      // elanındakı Android istinadı) — treyler/xəta rejimində menyuya düşmür.
      ticketGranted = true
      ticketSessionId = ticket.playbackSessionId || null
      startFullPlayback(ticket)
      // Sonluq qatı ulduzları göstərsinmi — cavab ORADA lazım olacaq, indi soruşulur.
      loadExistingRating(contentId)
      // Eyni anda baxış limiti + admin monitorinqi — oynatmadan SONRA, bloklamadan.
      openSession(contentId, profileId)
      // ⚠ BAXIŞ MÖVQEYİ (547) — sessiya ilə YANAŞI, amma AYRI iş: sessiya eyni
      //   anda baxış limitini sayır, bu isə «Davam et» sırasını və biletin
      //   `startPositionSeconds`-ini bəsləyir. Veb TV heç birini yazmırdı.
      startProgressReports(profileId)
    }

    void requestTicket(contentId, profileId, options.episodeId).then((result: PlaybackResult) => {
      /*
        2026-10-25: GEC GƏLƏN BİLETİN SESSİYASI BİTİRİLİR. Bilet sessiyanı serverdə özü açır;
          ekran sökülübsə (bilet gəlməmiş «Geri»), yeni cəhd başlayıbsa və ya profil dəyişibsə
          oynatma heç başlamayacaq. ƏVVƏL cavab sadəcə atılırdı və sessiya 180 s ekran sayına
          düşürdü.
      */
      if (destroyed || asked !== playbackAttempt) {
        if (result.status === 'ok') endTicketSession(result.ticket)
        return
      }
      if (result.status === 'unauthorized' && !readSession()) {
        const reason = reasonFor('unauthorized')
        showNotice(reason.title, reason.text, false)
        return
      }
      if (!isViewerCurrent()) {
        if (result.status === 'ok') endTicketSession(result.ticket)
        return
      }
      if (result.status === 'profileMissing') {
        forgetMissingProfile()
        return
      }
      if (result.status === 'ok' && hasDrm(result.ticket.licenseServers)) {
        // 2026-10-24: açar sistemi olmayan TV brauzeri sessiya AÇMADAN dayanır — səbəb
        // və ölçmə `lib/drmSupport.ts`-dədir. Cihaz build-i (Tizen/webOS) bu yoxlamadan keçmir.
        // 2026-10-25: biletin serverdə açdığı sessiya da bitirilir (`endTicketSession`) —
        //   əvvəl DRM-siz brauzer hər «İzlə»də 180 s bir ekran tuturdu.
        const granted = result
        void canPlayProtected(granted.ticket.licenseServers).then((supported) => {
          if (destroyed || asked !== playbackAttempt || !isViewerCurrent()) {
            endTicketSession(granted.ticket)
            return
          }
          if (supported) {
            beginGrantedPlayback(granted.ticket)
            return
          }
          endTicketSession(granted.ticket)
          reportMediaError('drm-probe', 6001, contentId)
          setText(hint, '')
          showNotice(T('Oynatma xətası'), unsupportedDrmText(), false)
        })
        return
      }
      if (result.status === 'ok') {
        beginGrantedPlayback(result.ticket)
        return
      }
      const reason = reasonFor(result.status)
      // 2026-10-19: hüquq olmayan filmdə 5 saniyəlik izah yox olurdu və yalnız
      // treyler qalırdı. Sahib qərarı: TV-də alış keçidi yoxdur; başqa cihazda
      // ödəniş barədə izah və hüququ yenidən yoxlama daimi görünür.
      if (result.status === 'forbidden') {
        setText(hint, '')
        showNotice(reason.title, reason.text, true, true)
        return
      }
      /*
        ⚠⚠ YAŞ VƏ ÖLKƏ RƏDDİNDƏ TREYLER YOXDUR — YALNIZ BİLDİRİŞ (2026-10-25, kod oxunuşu).
           ƏVVƏL bu iki rədd də `degradeToTrailer`-ə düşürdü: yaşı çatmayan istifadəçiyə 18+
           filmin treyleri TAM EKRANDA, SƏSLƏ oynayırdı (`startTrailer` → `muted = false`),
           server isə məhz bu səbəbdən bileti verməmişdi. Ölkə rəddində də treyler həmin
           ölkədə göstərilməməli olan materialdır. Qalan rədlər (tezliklə · hazır deyil)
           treyleri `trailerAllowed` yoxlaması ilə saxlayır (`degradeToTrailer`).
      */
      if (result.status === 'age' || result.status === 'country') {
        setText(hint, '')
        showNotice(reason.title, reason.text, reason.retry)
        return
      }
      degradeToTrailer(reason.title, reason.text, reason.retry)
    })
  }

  /**
   * Baxış sessiyasını açır (`POST /api/playback/sessions`) — biletin sessiyasını İDDİA edir.
   *
   * ⚠ SIXIŞDIRMA YALNIZ BURADA OLUR (2026-10-25 server qaydası): bilet başqa cihazı çıxarmır,
   *   limit dolubsa ən köhnə ekran bu çağırışda bağlanır. Ona görə gizlənmədən qayıdış da
   *   (`resumeSession`) eyni yoldan keçir.
   */
  function openSession(contentId: string, profileId: string): void {
    const asked = playbackAttempt
    const opening = playbackClosing.then(() =>
      destroyed || asked !== playbackAttempt || !isViewerCurrent() ? null :
        openPlaybackSession(contentId, profileId, options.episodeId, onSessionReplaced),
    )
    playbackOpening = opening
    void opening.then((handle) => {
      // `playbackOpening` dəyişibsə sessiya artıq buraxılıb — onu `releasePlaybackSession` bağlayır.
      if (destroyed || asked !== playbackAttempt || playbackOpening !== opening || !isViewerCurrent()) return
      playbackSession = handle
      // Video heç başlamayıbsa (avtoplay bloklanıb) pauza elə buradan sayılır.
      schedulePausedRelease()
    })
  }

  /** Bu oynadıcının hesabı hələ də cari hesabdırmı (profil fərqi nəzərə alınmır). */
  function sameAccount(): boolean {
    const current = readSession()
    return playbackOwnerGeneration === sessionGeneration() &&
      (playbackOwner ? playbackOwner.userId : null) === (current ? current.userId : null)
  }

  /** Oynatması başlamayacaq biletin serverdəki sessiyasını bitirir (izah `endPlaybackSession`). */
  function endTicketSession(ticket: PlaybackTicket): void {
    const id = ticket.playbackSessionId
    if (!id || !sameAccount()) return
    playbackClosing = playbackClosing.then(() => endPlaybackSession(id)).catch(() => undefined)
  }

  /**
   * SEÇİLMİŞ PROFİL SİLİNİB (bilet 404 `Profiles.NotFound`, 2026-10-25) — seçim və onun yaş
   * həddi silinir, «Kim baxır?» açılır. ƏVVƏL «video hələ hazırlanmayıb» əbədi qalırdı.
   */
  function forgetMissingProfile(): void {
    writeSelectedProfileId(null)
    applyViewerAgeLimit(null)
    stopPlayback()
    navigate({ name: 'profiles', replaceOnPick: true }, true)
  }

  /**
   * BİLET ALINMIŞDI, OYNATMA TƏRK EDİLİR (Shaka dəstəklənmir → treyler, ölümcül media xətası →
   * bildiriş). 2026-10-25, kod oxunuşu: cihaz skinində Shaka dəstəklənməyəndə treyler oynayırdı,
   * iddia olunmuş sessiya isə istifadəçi çıxana qədər heartbeat göndərirdi (bir ekran tuturdu);
   * 15 saniyəlik mövqe dövrü treylerin saniyəsini FİLMİN mövqeyi kimi yazırdı. İndi sessiya
   * bitirilir, dövr dayanır, gözləyən media işi (cəhd nömrəsi) ləğv olunur.
   */
  function abandonPlayback(): void {
    if (!ticketGranted && !ticketSessionId && !playbackSession && !playbackOpening) return
    playbackAttempt += 1
    releasePlaybackSession()
    stopProgressReports()
    ticketGranted = false
  }

  /**
   * SESSİYA BAŞQA CİHAZA KEÇDİ (409 `Playback.SessionReplaced`).
   *
   * ⚠⚠ VEB TV BU CAVABI UDURDU (13 avqust 2026-da tapıldı): sayt, iOS və Android
   *    12 avqustda düzəldilmişdi, TV isə heartbeat-in nəticəsinə ümumiyyətlə
   *    baxmırdı — server sessiyanı bağlayır, televizorda film isə oynamağa
   *    DAVAM EDİRDİ. Yəni «eyni anda 2 (standard) / 4 (premium) ekran» fərqi
   *    bu səthdə faktiki deyildi (`Docs/SubscriptionModel.md` §5).
   * ⚠ OYNATMA ƏVVƏLCƏ DAYANDIRILIR, sonra səbəb yazılır: video arxa fonda
   *   qalsaydı istifadəçi «mesaj var, amma film gedir» görərdi.
   * ⚠ «Yenidən cəhd et» AÇIQDIR (saytdakı «Burada davam et» ilə eyni məna):
   *   yeni bilet + yeni sessiya açılır və bu dəfə O BİRİ cihaz sıxışdırılır.
   */
  /*
    ⚠⚠ 2026-10-25: MEDİA SÖKÜLÜR, OYNADICI ÖLÜ QALIR (kod oxunuşu). ƏVVƏL burada yalnız
       `video.pause()` var idi: videoya klik / ⏯ (brauzer) və ya pultun ▶-i (cihaz) filmi
       SESSİYASIZ davam etdirirdi, 15 saniyəlik mövqe dövrü də yazmağa davam edirdi — limit
       yenə kağız üzərində qalırdı. İndi son mövqe yazılır, hls.js/Shaka sökülür, `src` silinir,
       dövr dayanır və `replaced` bayrağı oynat/sarıma/paneli söndürür. Çıxış yalnız «Yenidən
       cəhd et» (yeni bilet + `/sessions`) və «Geri»dir.
  */
  function onSessionReplaced(): void {
    if (destroyed || replaced) return
    flushProgress()
    // Əvəzlənmiş sessiya uzun pauza qaydasına düşmür və öz-özünə açılmır.
    cancelPausedRelease()
    replaced = true
    playbackAttempt += 1
    releasePlaybackSession()
    stopProgressReports()
    ticketGranted = false
    closePanel()
    leaveControlRow()
    stopCountdown()
    outroAfterEnd = false
    closeOutro()
    resetMedia()
    setText(hint, '')
    showNotice(
      T('Baxış başqa cihazda davam edir'),
      T('Planınızda eyni anda icazə verilən ekran sayı doldu. Digər cihazda izləməni dayandırıb burada davam edə bilərsiniz.'),
      true,
    )
  }

  function startFullPlayback(ticket: PlaybackTicket): void {
    clearNotice()
    const asked = playbackAttempt
    void mediaClosing.then(() => {
      if (destroyed || asked !== playbackAttempt || !isViewerCurrent()) return
      prepareFullPlayback(ticket)
    })
  }

  function prepareFullPlayback(ticket: PlaybackTicket): void {
    const start =
      typeof ticket.startPositionSeconds === 'number' && ticket.startPositionSeconds > 0
        ? ticket.startPositionSeconds
        : 0

    // DRM lisenziya serveri varsa → Shaka (EME); yoxdursa imzalı açıq HLS → native.
    if (hasDrm(ticket.licenseServers)) {
      startDrmPlayback(ticket, start)
    } else {
      video.muted = false
      setText(hint, '') // izah label-i YOXDUR (sahib tələbi) — yalnız yüklənmə mesajları qalır
      playHlsUrl(ticket.manifestUrl, start)
    }
  }

  // ── DRM (Shaka) ──────────────────────────────────────────────────────────
  function startDrmPlayback(ticket: PlaybackTicket, startAt: number): void {
    const asked = playbackAttempt
    setText(hint, T('Qorunan oynadıcı yüklənir…'))
    void loadShaka().then((shaka) => {
      if (destroyed || asked !== playbackAttempt || !isViewerCurrent()) return
      if (!shaka || !shaka.Player.isBrowserSupported()) {
        reportMediaError(shaka ? 'drm-unsupported' : 'drm-library', 0, options.contentId)
        // 2026-10-25: treylerə keçməzdən ƏVVƏL sessiya bitirilir və mövqe dövrü dayanır —
        //   izah `abandonPlayback`-dədir.
        abandonPlayback()
        // ⚠ Təkrar cəhd MƏNALIDIR: kitabxana CDN-dən gəlir (bax `lib/shaka.ts`)
        //   və ilk yükləmə şəbəkə səbəbindən boşa çıxa bilər — cihaz dəstəyi
        //   həqiqətən yoxdursa ikinci cəhd eyni mesajı verir, zərəri yoxdur.
        degradeToTrailer(
          T('Qorunan video dəstəklənmir'),
          T('Bu cihazda DRM oynatması mümkün olmadı.'),
          true,
        )
        return
      }

      const player = new shaka.Player()
      shakaPlayer = player

      void player.attach(video).then(() => {
        if (destroyed || asked !== playbackAttempt || shakaPlayer !== player || !isViewerCurrent()) return
        player.configure({
          drm: {
            servers: toShakaServers(ticket.licenseServers),
            advanced: ticket.fairplayCertificateUrl
              ? { 'com.apple.fps': { serverCertificateUri: ticket.fairplayCertificateUrl } }
              : undefined,
          },
          streaming: { bufferingGoal: 30, rebufferingGoal: 4 },
          // ⚠ Eyni səbəb (bax hls.js): Shaka da 500 kbit/s-dən başlayır və ilk
          //   dəqiqə 240p oynayır. Yalnız BAŞLANĞIC təxminidir — ABR sonra özü qərar verir.
          //   Dəyər `TV_STARTUP_BANDWIDTH`-dir (Android TV pariteti — faylın başına bax).
          // ⚠ `useNetworkInformation: false` MƏCBURİDİR, yoxsa yuxarıdakı dəyər
          //   İŞLƏMİR: Shaka `navigator.connection.downlink * 1e6`-ni ONUN YERİNƏ
          //   qoyur (mənbədə ölçüldü, 2026-08-05 — saytda 1.75 Mbit/s çıxırdı və
          //   film 480p açılırdı). TV brauzerlərində bu dəyər daha da etibarsızdır.
          abr: { defaultBandwidthEstimate: TV_STARTUP_BANDWIDTH, useNetworkInformation: false },
          // 2026-10-24: 1920×1080-dən böyük variant süzülür — televizor dekoderi onu aça bilmir
          // (Akinak 2048×858; ölçmə və səbəb `lib/videoCap.ts`-də). ƏVVƏL 10 Mbit başlanğıc
          // təxmini ilə ABR ilk seqmentdən ən böyük variantı seçə bilirdi.
          restrictions: SHAKA_TV_RESTRICTIONS,
        })

        // Qısa ömürlü playback tokeni (varsa) lisenziya sorğusuna əlavə olunur.
        if (ticket.licenseHeaders) {
          const engine = player.getNetworkingEngine()
          if (engine) {
            engine.registerRequestFilter((type, request) => {
              if (type !== shaka.net.NetworkingEngine.RequestType.LICENSE) return
              copyHeaders(request.headers, ticket.licenseHeaders)
            })
          }
        }

        /**
         * ABR pilləni dəyişəndə paneldəki «Avtomatik · 1080p» yenilənir (F2).
         * ⚠ İKİ HADİSƏ: `adaptation` ABR-ın öz qərarıdır, `variantchanged` isə
         *   əl ilə seçimdən sonra gəlir — yalnız birini dinləsək yazı bəzi
         *   keçidlərdə köhnə dəyərdə ilişib qalır.
         */
        player.addEventListener('adaptation', refreshQualityLabel)
        player.addEventListener('variantchanged', refreshQualityLabel)

        player.addEventListener('error', (event: Event) => {
          if (destroyed || asked !== playbackAttempt || shakaPlayer !== player || !isViewerCurrent()) return
          // ⚠ Kateqoriya hadisənin `detail`-indədir (Shaka `ErrorEvent`); köhnə
          //   nüsxədə olmaya bilər — o halda ümumi mesaja düşürük.
          const detail = (event as unknown as { detail?: { category?: number; code?: number; severity?: number } }).detail
          const category = detail && typeof detail.category === 'number' ? detail.category : 0
          reportMediaError('drm-event', detail && detail.code ? detail.code : 0, options.contentId)
          showNotice(T('Oynatma xətası'), shakaErrorText(category), true)
          // 2026-10-25: KRİTİK xəta (severity 2) oynatmanı dayandırır — sessiya bitirilir, Shaka
          //   sökülür. Bərpa olunan xətada (1) Shaka davam edir və sessiya qalır.
          if (detail && detail.severity === 2) {
            abandonPlayback()
            resetMedia()
          }
        })

        setText(hint, '') // izah label-i YOXDUR (sahib tələbi) — yalnız yüklənmə mesajları qalır
        video.muted = false
        player.load(ticket.manifestUrl, startAt > 0 ? startAt : undefined).then(
          () => {
            if (!destroyed && asked === playbackAttempt && shakaPlayer === player && isViewerCurrent()) void video.play().catch(() => undefined)
          },
          (error: unknown) => {
            /*
              ⚠⚠ SƏBƏB AYRILIR, ÜMUMİ MESAJ YOX (2026-08-22, saytdakı eyni defektin
                 qarşılığı — TestingResultClaude «SAYT 86»). ÖLÇÜLDÜ: Playwright
                 Chromium-unda (Widevine CDM YOXDUR) veb TV-də «Qorunan video
                 yüklənmədi» çıxırdı — istifadəçi bunu SERVER nasazlığı kimi oxuyur
                 və «yenidən cəhd» düyməsinə basıb-basıb dayanır. Halbuki səbəb
                 brauzerdədir və yeganə çıxış BAŞQA BRAUZERDİR.
              ⚠ Shaka `error.code` 6001 = REQUESTED_KEY_SYSTEM_CONFIG_UNAVAILABLE
                (brauzer bu açar sistemini ümumiyyətlə dəstəkləmir), 6000 isə açar
                sistemi qurula bilmədi — hər ikisi «bu brauzerdə olmaz» deməkdir.
                Qalan DRM kodları (lisenziya sorğusu, vaxt) SERVER/şəbəkə tərəfdir,
                onlarda köhnə mesaj qalır.
              ⚠ MƏTN CİHAZ SKİNİNDƏ FƏRQLİDİR: Tizen/webOS-da «başqa brauzer aç»
                mənasızdır — orada cihazın öz DRM-i danışır.
            */
            const shaka = error as { code?: number; category?: number } | undefined
            const code = shaka && typeof shaka.code === 'number' ? shaka.code : 0
            const category = shaka && typeof shaka.category === 'number' ? shaka.category : 0
            reportMediaError('drm-load', code, options.contentId)
            const unsupported = code === 6000 || code === 6001
            const text = unsupported
              ? unsupportedDrmText()
              : category === 6
                ? T('Qorunan videonun lisenziyası alınmadı. Bir azdan yenidən cəhd edin.')
                : T('Qorunan video yüklənmədi.')
            if (!destroyed && asked === playbackAttempt && shakaPlayer === player && isViewerCurrent()) {
              showNotice(T('Oynatma xətası'), text, !unsupported)
              // 2026-10-24: oynada bilməyən cihaz baxış yerini tutmasın — sessiya bağlanır,
              // Shaka sökülür (əvvəl heartbeat istifadəçi çıxana qədər gedirdi).
              // 2026-10-25: lisenziya/şəbəkə xətasında da (film onsuz da açılmadı) — «Yenidən
              //   cəhd et» yeni bilet və yeni sessiya ilə başlayır.
              abandonPlayback()
              resetMedia()
            }
          },
        )
      }).catch(() => {
        // 2026-10-04: Shaka attach rəddi yalnız yüklənmə yazısını saxlayırdı və
        // idarə olunmamış promise yaradırdı. İndi təkrar cəhd edilən aydın xəta var.
        if (!destroyed && asked === playbackAttempt && shakaPlayer === player && isViewerCurrent()) {
          showNotice(T('Oynatma xətası'), T('Qorunan video yüklənmədi.'), true)
          // 2026-10-25: film açılmadı — sessiya ekran yeri tutmasın (`abandonPlayback`).
          abandonPlayback()
        }
      })
    })
  }

  // ── Treyler rejimi ───────────────────────────────────────────────────────
  /**
   * @param onUnavailable treyler mənbəyi YOXDURSA və ya HƏLL OLUNMURSA çağırılır —
   *   verilməyibsə treylerin öz xəta bildirişi göstərilir. Filmin bileti alınmayanda
   *   (`degradeToTrailer`) çağıran bunu verir ki, ekranda ƏSL səbəb qalsın.
   */
  function playTrailer(asset: string, onUnavailable?: () => void): void {
    const source = resolveTrailer(asset)
    if (!source) {
      if (onUnavailable) onUnavailable()
      else showNotice(T('Treyler tapılmadı'), T('Bu məzmun üçün treyler mənbəyi yoxdur.'))
      return
    }
    if (source.kind === 'unsupported') {
      // Vimeo → serverdən açıq HLS həll et (birbaşa embed TV-də oynamır).
      if (asset.indexOf('vimeo:') === 0) {
        setText(hint, T('Treyler yüklənir…'))
        void resolveVimeoTrailer(options.slug).then((resolved) => {
          if (destroyed) return
          if (resolved && resolved.kind === 'hls') startTrailer(resolved)
          else if (onUnavailable) onUnavailable()
          else showNotice(T('Treyler oynadıla bilmədi'), T('Treyler mənbəyi həll olunmadı.'), true)
        })
        return
      }
      if (onUnavailable) {
        onUnavailable()
        return
      }
      // ⚠ `source.reason` DƏYİŞƏNDİR — `T()` onu lüğətdə axtarır, amma i18n örtük
      //   testi yalnız sabit mətnli çağırışları görür (regex mənbə kodunu skan edir).
      //   Ona görə hər iki mümkün dəyər lüğətə ƏL İLƏ yazılıb — bax `lib/trailer.ts`.
      showNotice(T('Treyler oynadıla bilmədi'), T(source.reason))
      return
    }

    startTrailer(source)
  }

  /** Həll olunmuş HLS treylerini oynadır (poster + səs + hint). */
  function startTrailer(source: Extract<TrailerSource, { kind: 'hls' }>): void {
    if (source.poster) video.setAttribute('poster', source.poster)
    video.muted = false
    setText(hint, '') // izah label-i YOXDUR (sahib tələbi) — yalnız yüklənmə mesajları qalır
    /**
     * ⚠ MODALDAKI SANİYƏDƏN DAVAM (sahib tələbi 2026-08-21: «fullscreen vuranda
     *   modaldakı player qaldığı yerdən davam etsin»). `playHlsUrl` ikinci
     *   parametri onsuz da başlanğıc mövqeyidir — tam filmdə «davam et» üçün
     *   işlədilir, treylerdə isə indiyə qədər həmişə 0 verilirdi.
     */
    const startAt =
      typeof options.trailerStartAt === 'number' && isFinite(options.trailerStartAt)
        ? Math.max(0, options.trailerStartAt)
        : 0
    playHlsUrl(source.url, startAt)
  }

  /**
   * DRM-SİZ HLS manifestini oynadır (treyler + imzalı tam film).
   *
   * ⚠ SIRA TƏRSİNƏ ÇEVRİLDİ (2026-08-05 auditi): MSE varsa **hls.js**, native
   *   `<video>` isə yalnız EHTİYAT. Əvvəl `canPlayHlsNatively()` doğru olan hər
   *   cihazda — yəni HƏR REAL TELEVİZORDA — birbaşa `video.src` yazılırdı və
   *   bunun iki ölçülə bilən nəticəsi var idi:
   *     · ⚙ panel «Bu videoda əlavə seçim yoxdur» yazırdı. Native yolda nə hls.js,
   *       nə Shaka obyekti var, yəni pillə siyahısı, altyazı və səs yolları bizim
   *       əlimizdə DEYİL → KINOBOX_PLAYER.md F1·F3·F4 real cihazda tamamilə ölü idi.
   *       Masaüstü dev brauzerində isə menyu İŞLƏYİRDİ (orada native HLS yoxdur və
   *       kod hls.js yoluna düşürdü) — qüsurun bu qədər uzun yaşamasının səbəbi budur.
   *     · başlanğıc bant genişliyi təxmini (K2, 6 Mbit) tətbiq olunmurdu: filmin
   *       açılışını TV-nin öz ABR-ı seçirdi və o, həmişə aşağıdan başlayır.
   *
   * ⚠ DRM YOLUNA TOXUNULMAYIB: şifrəli manifest bura ÜMUMİYYƏTLƏ gəlmir
   *   (`startFullPlayback` → `startDrmPlayback` → Shaka/EME). Bəzi Tizen/webOS
   *   modellərində MSE var, amma DRM native oynadıcı tələb edir — həmin seçim
   *   olduğu kimi qalır.
   *
   * ⚠ NATIVE EHTİYATI ÜÇ HALDA İŞƏ DÜŞÜR: MSE yoxdur (köhnə mühərrik) · hls.js
   *   CDN-dən gəlmədi (operator DNS-i, Tizen `<access>` siyahısı) · `isSupported()`
   *   yalan dedi. Hər üçündə `video.src` yazılır, yəni oynatma ƏVVƏLKİ KİMİ işləyir
   *   (menyusuz) — §6 «qara ekran yoxdur» qaydası pozulmur.
   */
  function playHlsUrl(url: string, startAt: number): void {
    const asked = playbackAttempt
    const playNative = (): void => {
      if (destroyed || asked !== playbackAttempt || !isViewerCurrent()) return
      // ⚠ Yüklənmə mesajı BURADA da silinir: hls.js gəlmədikdə «Video yüklənir…»
      //   film artıq oynayarkən ekranda ilişib qalırdı (MANIFEST_PARSED heç vaxt
      //   gəlmir, çünki kitabxana yoxdur).
      setText(hint, '')
      /**
       * ⚠ XƏTA BLOKU DA SİLİNİR (2026-08-05 nəzarətində tapıldı). hls.js-in ölümcül
       *   MEDIA xətası çox vaxt `<video>`-nun öz `error` hadisəsi ilə birlikdə gəlir
       *   və aşağıdakı dinləyici `showNotice('Oynatma xətası')` çağırır. Native
       *   ehtiyat yolu isə filmi UĞURLA oynadır — nəticədə işləyən filmin üstündə
       *   xəta bloku qalırdı. Daha pisi: `onKey`-də «bildiriş varsa Enter player-i
       *   bağlayır» qaydası var, yəni istifadəçi Enter basanda film bağlanırdı.
       */
      clearNotice()
      video.src = url
      if (startAt > 0) seekWhenReady(startAt)
      void video.play().catch(() => undefined)
    }

    // MSE yoxdursa hls.js onsuz da işləməz — CDN-dən kitabxana çəkməyin mənası
    // yoxdur, birbaşa TV-nin öz oynadıcısına veririk.
    if (!hasMediaSource()) {
      playNative()
      return
    }

    setText(hint, T('Video yüklənir…'))
    void loadHlsJs().then((Hls) => {
      if (destroyed || asked !== playbackAttempt || !isViewerCurrent()) return
      if (!Hls || !Hls.isSupported()) {
        // Kitabxana gəlmədi və ya mühərrik dəstəkləmir → native; alınmasa `video`
        // öz `error` hadisəsi ilə izahlı mesaj göstərir (boş ekran yox).
        playNative()
        return
      }
      // ⚠ KEYFİYYƏT (sahib bildirişi 2026-08-04: «tvdə test üçün açırıq video
      //   keyfiyyətsiz görünür»). SƏBƏB: hls.js-in default bant genişliyi təxmini
      //   ~500 kbit/s-dir — yəni İLK seqment həmişə ƏN AŞAĞI renditiondan (240p)
      //   götürülür və ABR yuxarı qalxana qədər 15-30 saniyə keçir. TV-də
      //   istifadəçi filmi elə o pəncərədə görür və «keyfiyyətsiz» qərarını verir.
      //   Kinobox TV-ləri kabel/Wi-Fi 5 ilə işləyir; şəbəkə zəifdirsə ABR onsuz
      //   da BİR seqmentdən sonra enir. Dəyər `TV_STARTUP_BANDWIDTH` = 10 Mbit-dir
      //   (522 — Android TV ilə eyni; 6 Mbit 0.7 büdcə əmsalı ilə 1080p pilləsini
      //   kənarda qoyurdu, faylın başındakı izaha bax).
      // ⚠ `startLevel` SABİTLƏNMİR: sabit yüksək səviyyə zəif şəbəkədə filmi
      //   bufferə salardı. Yalnız başlanğıc TƏXMİNİ dəyişir, qərarı ABR verir.
      // ⚠ İNDİ BU SƏTİR REAL TV-DƏ DƏ İCRA OLUNUR: əvvəl televizor native yola
      //   düşdüyü üçün başlanğıc təxmini yalnız masaüstü dev brauzerinə tətbiq olunurdu.
      const player = new Hls({
        abrEwmaDefaultEstimate: TV_STARTUP_BANDWIDTH,
        maxBufferLength: 30,
      })
      hls = player

      /**
       * ÖLÜMCÜL XƏTA — İKİ FƏRQLİ VƏZİYYƏT.
       *
       * ⚠ AÇILIŞDA UĞURSUZLUQ → NATIVE EHTİYATI (bir dəfə). hls.js artıq TV-də
       *   ƏSAS yoldur, yəni onun açılışda sınması birbaşa «film açılmır» deməkdir.
       *   TV-nin öz oynadıcısı isə MSE-nin dəstəkləmədiyi kodekləri (məsələn HEVC)
       *   aça bilir — bu keçid həmin halı xilas edir.
       * ⚠ OYNAMAĞA BAŞLAYANDAN SONRA UĞURSUZLUQ → MESAJ, ehtiyat YOX. `video.src`
       *   yazmaq filmi BAŞDAN başladar: ortada şəbəkə düşəndə istifadəçi 40-cı
       *   dəqiqədən 0-a atılardı. Belə halda izahlı mesaj göstərilir (§6).
       * ⚠ BAYRAQ MƏCBURİDİR: hls.js ardıcıl bir neçə ölümcül xəta göndərə bilir —
       *   bayraqsız `playNative()` dəfələrlə çağırılıb `video.src`-i sıfırlayardı.
       */
      let nativeTried = false
      /*
        ⚠ ƏVVƏLCƏ BƏRPA, SONRA MESAJ (2026-09-26). ƏVVƏL: oynamağa başlayandan sonra hər
           ölümcül xəta dərhal «Oynatma kəsildi» + əl ilə təkrar (bütün bilet axını yenidən)
           idi — TV Wi-Fi-ının bir neçə saniyəlik kəsintisi filmi dayandırırdı. hls.js-in öz
           tövsiyəsi: şəbəkə xətasında `startLoad()` (mövqe qalır), media xətasında
           `recoverMediaError()`, ikinci dəfə `swapAudioCodec()` ilə. Sayğaclar bu
           nüsxəyə aiddir; hədd aşılanda köhnə mesaj yolu qalır.
      */
      let networkRetries = 0
      let mediaRecoveries = 0
      player.on(Hls.Events.ERROR, (_e, data) => {
        if (!data.fatal || destroyed) return
        const networkType = Hls.ErrorTypes ? Hls.ErrorTypes.NETWORK_ERROR : undefined
        reportMediaError(data.type === networkType ? 'hls-network' : 'hls-media', 0, options.contentId)
        if (!hasPlayed && !nativeTried) {
          nativeTried = true
          player.destroy()
          if (hls === player) hls = null
          playNative()
          return
        }
        const types = Hls.ErrorTypes
        if (types && data.type === types.NETWORK_ERROR && player.startLoad && networkRetries < 3) {
          networkRetries++
          window.setTimeout(() => {
            if (!destroyed && hls === player && player.startLoad) player.startLoad()
          }, 1000 * networkRetries)
          return
        }
        if (types && data.type === types.MEDIA_ERROR && player.recoverMediaError && mediaRecoveries < 2) {
          mediaRecoveries++
          if (mediaRecoveries === 2 && player.swapAudioCodec) player.swapAudioCodec()
          player.recoverMediaError()
          return
        }
        // ⚠ SƏBƏBƏ GÖRƏ MESAJ (547) — bax `hlsErrorText`; əvvəl hər ölümcül xəta
        //   eyni ümumi cümləni verirdi.
        showNotice(T('Oynatma kəsildi'), hlsErrorText(data.type), true)
        // 2026-10-25: bərpa cəhdləri bitib — sessiya və mövqe dövrü dayanır (`abandonPlayback`).
        abandonPlayback()
      })
      player.on(Hls.Events.MANIFEST_PARSED, () => {
        // 2026-10-24: 1920×1080-dən böyük pillə ilk seqmentdən ƏVVƏL çıxarılır — televizor
        // dekoderi onu aça bilmir (prod: LG NetCast MEDIA_ERR_DECODE; səbəb `lib/videoCap.ts`).
        // ƏVVƏL 10 Mbit başlanğıc təxmini ilə hls.js ilk seqmentdən ən böyük pilləni seçə bilirdi.
        capHlsLevels(player)
        setText(hint, '') // izah label-i YOXDUR (sahib tələbi) — yalnız yüklənmə mesajları qalır
        if (startAt > 0) seekWhenReady(startAt)
        void video.play().catch(() => undefined)
      })
      // Pillə dəyişəndə paneldəki «Avtomatik · 1080p» yazısı yenilənir (F2).
      const switched = Hls.Events.LEVEL_SWITCHED
      if (switched) player.on(switched, refreshQualityLabel)

      player.loadSource(url)
      player.attachMedia(video)
    })
  }

  /** «Davam et» mövqeyinə keçir — metadata gələn kimi (native/hls.js yolu üçün). */
  function seekWhenReady(seconds: number): void {
    const doSeek = () => {
      video.removeEventListener('loadedmetadata', doSeek, false)
      try {
        video.currentTime = seconds
      } catch {
        // seek bəzi mənbələrdə metadata gələnə qədər atılır — best-effort.
      }
    }
    // `{ once: true }` seçimi köhnə TV-də (Chrome 38) yoxdur — əl ilə idarə olunur.
    if (video.readyState >= 1 && video.duration) doSeek()
    else video.addEventListener('loadedmetadata', doSeek, false)
  }

  // Bilet alınmayanda: treyler varsa onu oynat + səbəb banneri, yoxsa yalnız izah.
  //
  // ⚠ TREYLER OYNAYAN HALDA İZAH DAİMİ BLOK DEYİL, 5 SANİYƏLİK LENTDİR (522;
  //   Android `TvPlayerScreen`: «Treyler — {səbəb}» yuxarıda, `NOTICE_VISIBLE_MS`
  //   = 5 san sonra sönür — sahib 2026-08-06: «playerdə artıq sözlərə gərək
  //   yoxdur»). Əvvəl `showNotice` bloku ekranın ortasında treylerin üstündə
  //   ƏBƏDİ qalırdı. Siçan istifadəçisinin çıxışı onsuz da ✕ (`player__close`)
  //   və pultun Geri düyməsidir.
  // ⚠ VİDEO ÜMUMİYYƏTLƏ OLMAYAN HALDA daimi blok + «‹ Geri» QALIR (§6 dead-end
  //   qaydası) — orada gizlənən izah ekranı boş qoyardı.
  /*
    ⚠⚠ TREYLER HƏLL OLUNMASA ƏSL SƏBƏB QAYIDIR (2026-10-01, TestingAI2 veb TV 50).
       NƏ ÖLÇÜLDÜ (tv.kinobox.az, E2E hesabı, birdəfəlik satılan «Cin Atı» — 3 AZN,
       `trailerUrl = vimeo:…`): bilet ucu 403 `Playback.NoEntitlement` qaytardı,
       ekran treylerə düşdü, `…/cin-ati/trailer` isə 404 verdi. Nəticədə ekranın
       ortasında «Treyler oynadıla bilmədi · Treyler mənbəyi həll olunmadı» və
       «Yenidən cəhd et» qaldı, altda «Treyler yüklənir…» yazısı asılı qaldı;
       «Abunəlik lazımdır» yalnız 5 saniyəlik lentdə göründü və söndü.
       ƏVVƏL: istifadəçi filmi niyə izləyə bilmədiyini görmürdü, «təkrar cəhd» isə
       eyni 403-ə aparırdı. Eyni gün `…/trailer` ucu cin-ati, meryem, senin-ucun,
       cat-kapi-ask, mozalan, zona kontentində də 404 verdi.
       NİYƏ BU HƏLL: treyler yalnız bonusdur — alınmayanda ekran treyleri olmayan
       filmdəki kimi davranır (daimi səbəb bloku + «‹ Geri»), lent söndürülür.
  */
  /*
    2026-10-25: fallback treyleri də `trailerAllowed`-dən keçir — «tezliklə» rəddi serverdə yaş
      yoxlamasından ƏVVƏL gəlir, yəni 18+ filmin treyleri yaşı çatmayana tam ekranda açılardı.
  */
  function degradeToTrailer(title: string, text: string, retryable?: boolean): void {
    if (options.trailerAsset && trailerAllowed(options.ageLimit)) {
      // ⚠ Lent ƏVVƏL göstərilir: mənbə sinxron alınmayanda geri çağırış dərhal
      //   işləyir və lenti söndürməlidir — sıra tərs olsa lent blokun üstündə qalardı.
      showBanner(T('Treyler') + ' — ' + title + '. ' + text)
      playTrailer(options.trailerAsset, () => {
        if (bannerTimer !== null) {
          clearTimeout(bannerTimer)
          bannerTimer = null
        }
        banner.style.display = 'none'
        setText(hint, '')
        showNotice(title, text, retryable)
      })
    } else {
      setText(hint, '') // bildiriş blokunda kliklənən «‹ Geri» var — təkrar izah lazım deyil
      showNotice(title, text, retryable)
    }
  }

  /**
   * @returns `retry` — «Yenidən cəhd et» düyməsi göstərilsinmi. Yaş/abunə/giriş
   *   xətalarında təkrar sorğu EYNİ cavabı gətirər (bax `showNotice` qeydi).
   */
  function reasonFor(
    status: 'unauthorized' | 'forbidden' | 'age' | 'country' | 'comingSoon' | 'unavailable',
  ): { title: string; text: string; retry: boolean } {
    if (status === 'country') {
      return { title: T('Oynatma hazır deyil'), text: T('Bu film olduğunuz ölkədə əlçatan deyil.'), retry: false }
    }
    if (status === 'unauthorized') {
      // ⚠ Başlıq da `T()`-dən keçir — əvvəl xam sətir idi və ingilis/rus/türk
      //   dilində «Daxil olun» olaraq qalırdı (mətn tərcümə olunurdu, başlıq yox).
      return {
        title: T('Daxil olun'),
        text: T('İzləmək üçün Profil bölməsindən QR ilə daxil olun.'),
        retry: false,
      }
    }
    // ⚠ Yaş həddində «abunə ol» YAZILMIR: abunə problemi həll etmir və istifadəçini
    //   ödəniş axınına göndərmək yanıldıcıdır.
    // ⚠ TARİXÇƏ: 2026-08-05-də sahib TV-də tarix soruşulmamasını istəmişdi və mətn
    //   telefona/sayta yönləndirirdi; 2026-08-07-də qərar DƏYİŞDİ (MYTasks 530:
    //   «saytda oldugu kimi düzəliş imkanı olsun») — tarix indi film modalında
    //   pultla daxil edilir (`ui/detailModal.ts`). Server üç yaş kodunu da eyni
    //   `age` statusuna yığır (`lib/playback.ts`), ona görə hansı mətnin doğru
    //   olduğu client-dəki yaşdan çıxarılır: yaş BİLİNMİRSƏ tarix istənilir
    //   (modal bunu edir), bilinirsə «açılmır» — «başqa tarix yazsam açılar»
    //   təəssüratı yaranmasın.
    if (status === 'age') {
      return {
        title: T('Yaş həddi'),
        text:
          evaluateAge(AGE_GATE_FROM) === 'birthDate'
            ? T('Bu kontent yaş həddlidir. Davam etmək üçün doğum tarixinizi qeyd edin.')
            : T('Bu kontent yaş həddinə görə hesabınızda açılmır.'),
        retry: false,
      }
    }
    // ⚠⚠ «TEZLİKLƏ» AYRI MESAJDIR (sahib bildirişi, MYTasks 851: «Akinak filmi
    //   samsung modelində film izlə vururam drm və ya server xətası verir ama digər
    //   filmlər açır»). ÖLÇÜLDÜ 2026-08-24 canlı API-da: `akinak` üçün
    //   `isComingSoon = true`, bilet ucu 403 `Playback.ComingSoon` qaytarır.
    //   Əvvəl bu cavab `forbidden`-lə eyni yola düşürdü və ekranda «Abunəlik
    //   lazımdır» yazılırdı — abunə isə heç nəyi dəyişmir, film hələ dərc olunmayıb.
    if (status === 'comingSoon') {
      return {
        title: T('Tezliklə'),
        text: T('Bu film hələ əlçatan deyil — hələlik yalnız treylerini izləyə bilərsiniz.'),
        retry: false,
      }
    }
    if (status === 'forbidden') {
      return {
        title: T('Abunəlik lazımdır'),
        text: T('Abunə olmaq və ya filmi almaq üçün mobil cihazdan və ya kompüterdən kinobox.az saytına daxil olun. Sonra TV-də eyni hesabla izləyə bilərsiniz.'),
        retry: false,
      }
    }
    // ⚠ `unavailable` ŞƏBƏKƏ XƏTASINI DA ÖRTÜR (`lib/playback.ts` timeout/parse
    //   yolu) — məhz burada təkrar cəhdin mənası var.
    return {
      title: T('Oynatma hazır deyil'),
      text: T('Bu kontent üçün video hələ hazırlanmayıb.'),
      retry: true,
    }
  }

  /**
   * MÜHƏRRİK XƏTASI → İSTİFADƏÇİ MESAJI (547 — Android `KinoboxPlayer.kt` →
   * `playbackErrorMessage` cədvəlinin veb qarşılığı).
   *
   * ⚠ ƏVVƏL HƏR XƏTA EYNİ CÜMLƏNİ VERİRDİ («Video mənbəyinə çatmaq alınmadı»).
   *   İstifadəçi üçün fərq real: şəbəkə düşübsə internetə baxmalıdır, DRM
   *   lisenziyası alınmayıbsa gözləyib təkrar açmalıdır, kodek dəstəklənmirsə
   *   nə etsə də açılmayacaq. Saytda bu təsnifat HESABLANIR, amma UI-da atılır
   *   (`KinoboxPlayer.ts → layer`); Android TV-də isə tam cədvəl var — etalon odur.
   * ⚠ Shaka kateqoriyaları (`shaka.util.Error.Category`): 1 NETWORK · 3 MEDIA ·
   *   4 MANIFEST · 6 DRM. Rəqəmlər kitabxananın ictimai sabitləridir; obyektin
   *   özünü import etmirik (Shaka CDN-dən gəlir və tiplənmir).
   */
  function shakaErrorText(category: number): string {
    if (category === 6) return T('Qorunan videonun lisenziyası alınmadı. Bir azdan yenidən cəhd edin.')
    if (category === 1) return T('Şəbəkə bağlantısı kəsildi. İnterneti yoxlayıb yenidən cəhd edin.')
    if (category === 4) return T('Video mənbəyi tapılmadı.')
    if (category === 3) return T('Video formatı bu cihazda oynadılmır.')
    return T('Video oynadılmadı. Yenidən cəhd edin.')
  }

  /** hls.js `data.type` → eyni cədvəl (kitabxana kateqoriyanı sətirlə verir). */
  function hlsErrorText(type: string | undefined): string {
    if (type === 'networkError') return T('Şəbəkə bağlantısı kəsildi. İnterneti yoxlayıb yenidən cəhd edin.')
    if (type === 'mediaError') return T('Video formatı bu cihazda oynadılmır.')
    return T('Video oynadılmadı. Yenidən cəhd edin.')
  }

  /**
   * Saniyəni `s:ss` / `s:ss:ss` formatına salır.
   *
   * ⚠ `Intl`/`toLocaleTimeString` İŞLƏDİLMİR — köhnə TV mühərriklərində (Chrome 38)
   *   `Intl` ya yoxdur, ya da natamamdır; `padStart` da ES2017-dir (hədd ES2015).
   */
  function formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds) || seconds < 0) return '0:00'
    const total = Math.floor(seconds)
    const h = Math.floor(total / 3600)
    const m = Math.floor((total % 3600) / 60)
    const s = total % 60
    const ss = s < 10 ? '0' + s : String(s)
    if (h > 0) return h + ':' + (m < 10 ? '0' + m : String(m)) + ':' + ss
    return m + ':' + ss
  }

  /** Sonuncu yazılan mətnlər — eyni dəyəri təkrar yazmamaq üçün (saniyədə 4 hadisə gəlir). */
  let lastNowText = ''
  let lastTotalText = ''

  /** Zolağı və vaxtı VERİLMİŞ saniyəyə görə boyayır (sarımada hədəf, adi halda cari). */
  function paintProgress(seconds: number): void {
    if (!video.duration || isNaN(video.duration)) return
    fill.style.width = (seconds / video.duration) * 100 + '%'
    if (options.live) return

    const nowText = formatTime(seconds)
    if (nowText !== lastNowText) {
      lastNowText = nowText
      setText(timeNow, nowText)
    }
    const totalText = formatTime(video.duration)
    if (totalText !== lastTotalText) {
      lastTotalText = totalText
      setText(timeTotal, totalText)
    }
  }

  // Progress bar + vaxt — həqiqi video vaxtından.
  // ⚠ SARIMA GEDİRSƏ ÖTÜRÜLÜR: zolaq həmin an HƏDƏFİ göstərir (bax `seekBy`),
  //   `timeupdate` isə hələ köhnə mövqeni verir və göstərici titrəyərdi.
  video.addEventListener('timeupdate', () => {
    if (seekPending) return
    paintProgress(video.currentTime)
    // Sonluq qatının astanası — izah `checkOutro`-dadır.
    checkOutro()
  })

  /**
   * BUFERLƏNMİŞ HİSSƏ — `progress` hadisəsində (yükləmə davam edərkən) yazılır.
   *
   * ⚠ SONUNCU ARALIQ DEYİL, PLAYHEAD-İ ƏHATƏ EDƏN aralıq götürülür: seek-dən
   *   sonra qabaqda «ada» bufer qala bilir və zolaq filmin sonuna qədər dolu
   *   görünərdi — yalan göstərici bufer gözləməsini izahsız edir.
   * ⚠ Yalnız DƏYİŞƏNDƏ yazılır (`lastBufferedWidth`): `progress` saniyədə bir
   *   neçə dəfə gəlir, eyni dəyəri təkrar yazmaq TV-də boş paint deməkdir.
   */
  let lastBufferedWidth = ''
  function paintBuffered(): void {
    const duration = video.duration
    if (!duration || isNaN(duration) || !isFinite(duration)) return
    const ranges = video.buffered
    if (!ranges || ranges.length === 0) return
    const time = video.currentTime
    let end = 0
    for (let i = 0; i < ranges.length; i++) {
      if (ranges.start(i) <= time + 0.5 && ranges.end(i) > end) end = ranges.end(i)
    }
    const width = Math.max(0, Math.min(100, (end / duration) * 100)) + '%'
    if (width === lastBufferedWidth) return
    lastBufferedWidth = width
    buffered.style.width = width
  }
  video.addEventListener('progress', paintBuffered, false)
  video.addEventListener('seeked', paintBuffered, false)

  // Oynat/dayandır vəziyyəti — nişan yalnız vəziyyət dəyişəndə yazılır.
  // ⚠ İDARƏ SƏTRİNDƏKİ İKON DA BURADAN DƏYİŞİR (kliklə deyil): video başqa
  //   səbəbdən də dayana bilər (bufer, `ended`, şəbəkə) — ikonu yalnız klikdə
  //   çevirsəydik göstərici həqiqi vəziyyətdən ayrılardı.
  video.addEventListener('pause', () => {
    if (!destroyed && !options.live) pausedBadge.style.display = 'block'
    iconPause.style.display = 'none'
    iconPlay.style.display = 'inline-block'
    // Uzun pauza sayğacı (izah `releaseForLongPause`). `ended` da əvvəlcə `pause` verir.
    schedulePausedRelease()
  })
  video.addEventListener('play', () => {
    cancelPausedRelease()
    pausedBadge.style.display = 'none'
    iconPlay.style.display = 'none'
    iconPause.style.display = 'inline-block'
  })
  /**
   * Bitəndə: SERİALDA növbəti bölümün GERİ SAYIMI, filmdə/treylerdə geri.
   *
   * ⚠ TETİKLƏYİCİ YALNIZ `ended`-dir (sahib tələbi: «serial tam bitməmiş
   *   keçirmə, qoy bitsin sonra keçir») — faiz və ya «son N saniyə» əsaslı
   *   erkən keçid YOXDUR. Sayım bölüm BİTDİKDƏN sonra başlayır, yəni həmin
   *   qayda pozulmur.
   * ⚠ ANİ KEÇİD ARTIQ YOXDUR (547): əvvəl `ended` gələn kimi ekran replace
   *   olunurdu və istifadəçinin dayandırmağa imkanı yox idi. Saytda bu, 5
   *   saniyəlik geri sayım kartıdır (`WatchClient.tsx → NEXT_EPISODE_SECONDS`)
   *   — «İndi oynat» / «Ləğv et» seçimi ilə.
   * ⚠ `navigate(..., true)` = REPLACE: yığında hər bölüm üçün yeni sətir
   *   yığılmır (bax `switchToEpisode`).
   */
  video.addEventListener('ended', () => {
    if (destroyed) return
    if (options.live) {
      pollLiveProgram()
      return
    }
    flushProgress()
    if (upcoming && ticketGranted) {
      startNextCountdown()
      return
    }
    /*
      ⚠⚠ FİLMDƏ EKRAN BAĞLANMIR — SONLUQ QATI QALIR/AÇILIR (sahib tələbi
         2026-08-21, Android TV-də də eynidir): əvvəl bu budaq filmdə birbaşa
         `leavePlayer()` edirdi, yəni film bitən kimi istifadəçi modala atılırdı
         və nə qiymət verə bilirdi, nə də təklif görürdü.
      ⚠ ASTANA YOLU İLƏ AÇILIBSA TƏKRAR AÇILMIR (`outroShown`); istifadəçi qatı
        Geri ilə bağlayıbsa fikri bəllidir — film bitəndə çıxılır.
    */
    if (ticketGranted && options.contentId && !options.episodeId) {
      if (outro) return
      outroAfterEnd = true
      openOutro()
      if (outro) return
      // Qat açılmadı (artıq göstərilib və ya bağlanıb) — adi çıxış yolu qalır.
      outroAfterEnd = false
    }
    leavePlayer()
  })
  video.addEventListener('error', () => {
    // Native `<video>` mənbə xətası. Shaka öz `error` hadisəsini işlədir (video src
    // blob-dur) — ikiqat bildiriş olmasın deyə Shaka aktiv olanda ötürülür.
    if (!destroyed && !shakaPlayer && (video.src || options.trailerAsset)) {
      const code = video.error ? video.error.code : 0
      reportMediaError('native-video', code, options.contentId)
      // 2026-10-24 (prod: LG NetCast `native-video code=3` ×2): 3 = DECODE, 4 = SRC_NOT_SUPPORTED —
      // format/dekoder problemidir, «mənbəyə çatmaq alınmadı» istifadəçini interneti yoxlamağa
      // göndərirdi. Kod 2 (NETWORK) şəbəkə mətnini alır; qalanında köhnə mətn qalır.
      showNotice(
        T('Oynatma xətası'),
        code === 3 || code === 4 ? hlsErrorText('mediaError') : code === 2 ? hlsErrorText('networkError') : T('Video mənbəyinə çatmaq alınmadı.'),
        true,
      )
      // 2026-10-25: native mənbə xətası oynatmanı dayandırıb — sessiya ekran yeri tutmasın.
      //   hls.js qoşuludursa QƏRARI O VERİR: element xətası onun bərpası (`recoverMediaError`)
      //   ilə birlikdə gəlir və film davam edə bilər; tükənəndə hls budağı özü buraxır.
      if (!hls) abandonPlayback()
    }
  })

  /**
   * Bildiriş bloku — oynatma alınmayanda səbəbi göstərir.
   *
   * ⚠ «GERİ» DÜYMƏSİ MƏCBURİDİR (2026-08-03 real şikayət: «ekranda playback not
   *   ready yazır»). Əvvəl blokda YALNIZ başlıq + mətn var idi. Pultla baxan
   *   istifadəçi Back basıb çıxırdı, amma VEB TV-də siçanla baxan üçün çıxış yolu
   *   ÜMUMİYYƏTLƏ YOX İDİ: player fullscreen-dir, sol rail çəkilmir və klikləyəcək
   *   heç nə yoxdur — ekran dead-end olurdu (§6 «boş/qara ekran yoxdur» qaydası).
   */
  /**
   * Bildirişin düymələri — soldan sağa. Fokus indeksi `noticeIndex`-dədir.
   *
   * ⚠⚠ «YENİDƏN CƏHD ET» 547-DƏ ƏLAVƏ OLUNDU. Əvvəl blokda TƏK «Geri» vardı, yəni
   *   ANLIQ şəbəkə nasazlığında (TV Wi-Fi yuxudan çıxır — bax `compat.ts`) yeganə
   *   yol player-dən çıxıb filmi yenidən açmaq idi. Hər iki etalonda da düymə
   *   yoxdur (sayt `retryKey`-i yalnız ödəniş/doğum tarixindən sonra artırır,
   *   Android TV-də ümumiyyətlə yoxdur) — bu, ETALONDAN İRƏLİ gedən bənddir və
   *   §6 «dead-end yoxdur» qaydasının birbaşa davamıdır.
   * ⚠ YALNIZ MƏNALI OLANDA göstərilir: yaş həddi, abunə və giriş xətalarında
   *   təkrar cəhd EYNİ cavabı gətirər — düymə «app sınıb» kimi oxunardı.
   * ⚠ ÜÇ `let` YUXARIDA, DİGƏR STATE İLƏ BİR YERDƏ ELAN OLUNUR — səbəb orada
   *   («TDZ» qeydi): `showNotice` mənbəsiz ekranda mount vaxtı çağırılır.
   */

  function noticeItems(): Array<{ node: HTMLElement; run: () => void }> {
    const list: Array<{ node: HTMLElement; run: () => void }> = []
    if (noticeRetry) list.push({ node: noticeRetry, run: retryPlayback })
    if (noticeBack) list.push({ node: noticeBack, run: leavePlayer })
    return list
  }

  function paintNotice(): void {
    const items = noticeItems()
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item) setClass(item.node, 'is-focused', i === noticeIndex)
    }
  }

  function showNotice(title: string, text: string, retryable?: boolean, externalPayment?: boolean): void {
    if (!notice) {
      notice = el('div', 'empty')
      notice.style.top = '360px'
      page.appendChild(notice)
    }
    setText(notice, '')
    notice.appendChild(el('p', 'empty__title', title))
    notice.appendChild(el('p', 'empty__text', text))

    const actions = el('div', 'player__notice-actions')
    noticeRetry = null
    if (retryable) {
      noticeRetry = el('div', 'btn btn--primary focusable player__notice-btn', externalPayment ? T('Ödənişdən sonra yenidən yoxla') : T('Yenidən cəhd et'))
      actions.appendChild(noticeRetry)
    }
    noticeBack = el('div', 'btn focusable player__notice-btn', T('‹  Geri'))
    actions.appendChild(noticeBack)
    /*
      ⚠ BLOKUN DÜYMƏLƏRİ SİÇANLA DA BASILIR (brauzer variantı). Blokun öz
        şərhində «siçanla baxan üçün çıxış yolu ÜMUMİYYƏTLƏ YOX İDİ» yazılıb —
        düymələr çəkilirdi, amma heç bir klik dinləyicisi yox idi, yəni izah
        ekranı işarəçi ilə HƏQİQƏTƏN dead-end idi (§6 pozuntusu).
    */
    if (IS_BROWSER) {
      if (noticeRetry) clickable(noticeRetry, retryPlayback)
      clickable(noticeBack, leavePlayer)
    }
    notice.appendChild(actions)
    noticeIndex = 0
    paintNotice()

    // Bildiriş görünəndə chrome-un avtomatik gizlənməsi dayandırılır — əks halda
    // 5 saniyədən sonra səbəb mətni də sönərdi (o, `player__ui`-dan kənardadır,
    // amma istifadəçi «ekran donub» təəssüratı alır).
    showChrome()
  }

  function clearNotice(): void {
    if (notice && notice.parentNode) notice.parentNode.removeChild(notice)
    notice = null
    noticeBack = null
    noticeRetry = null
    noticeIndex = 0
  }

  /**
   * Oynatmanı SIFIRDAN qurur — ekrandan çıxmadan (547).
   *
   * ⚠ MÜHƏRRİK TAM SÖKÜLÜR: hls.js/Shaka nüsxəsi ölümcül xətadan sonra bərpa
   *   olunmur, üstünə yenisini qursaq iki dekoder eyni `<video>`-ya yazar.
   * ⚠ `hasPlayed` da sıfırlanır — «açılışda sındı → native ehtiyatı» qərarı
   *   (`playHlsUrl`) təzə cəhd üçün yenidən keçərli olmalıdır.
   */
  function retryPlayback(): void {
    if (destroyed) return
    flushProgress()
    cancelPausedRelease()
    playbackAttempt += 1
    releasePlaybackSession()
    clearLiveLoops()
    clearNotice()
    // Yeni bilet + yeni sessiya — sıxışdırılma/gizlənmə vəziyyəti burada bitir.
    replaced = false
    suspended = false
    hasPlayed = false
    ticketGranted = false
    lastReportedSecond = -1
    resetMedia()
    if (options.live) attachLive()
    else if (options.contentId) attachFullContent(options.contentId)
    else if (options.trailerAsset) playTrailer(options.trailerAsset)
  }

  /**
   * Açar sistemi yoxdur (Shaka 6000/6001 və ya `drmSupport` yoxlaması).
   * ⚠ TV BRAUZERİNƏ «başqa brauzer aç» DEMİRİK (2026-10-24, prod: LG NetCast Chrome/79):
   *   televizorda Chrome/Edge yoxdur — çıxış Kinobox tətbiqi və ya başqa cihazdır.
   */
  function unsupportedDrmText(): string {
    if (!IS_BROWSER) return T('Bu cihaz qorunan videonu oynada bilmir.')
    return isTvUserAgent(navigator.userAgent)
      ? T('Bu televizorun brauzeri qorunan videonu oynada bilmir. Filmi Kinobox tətbiqində və ya başqa cihazda (telefon, kompüter) izləyin.')
      : T('Bu brauzer qorunan videonu oynada bilmir. Başqa brauzerdə (Chrome, Edge, Safari) açın.')
  }

  function resetMedia(): void {
    video.pause()
    if (hls) {
      hls.destroy()
      hls = null
    }
    if (shakaPlayer) {
      const old = shakaPlayer
      shakaPlayer = null
      // Eyni video elementini yeni mühərrik almamış köhnə Shaka onu buraxmalıdır.
      mediaClosing = mediaClosing.then(() => old.destroy()).catch(() => undefined)
    }
    video.removeAttribute('src')
    video.load()
  }

  /**
   * Sessiyanı buraxır: heartbeat dayanır, server sətri bitirilir.
   *
   * ⚠ BİLETİN SESSİYASI DA (2026-10-25): `/sessions` iddiası alınmayıbsa (şəbəkə, ekran tez
   *   söküldü) handle yoxdur, amma bilet serverdə sessiya açıb — o, `ticketSessionId` ilə
   *   ayrıca bitirilir. İddia biletin sətrini götürübsə id eynidir, ikinci sorğu getmir.
   * @param keepalive səhifə gizlənir/bağlanır — növbə gözlənilmir, sorğular `keepalive` ilə gedir.
   */
  function releasePlaybackSession(keepalive?: boolean): void {
    const active = playbackSession
    const opening = playbackOpening
    const ticketId = ticketSessionId
    const ownAccount = sameAccount()
    playbackSession = null
    playbackOpening = null
    ticketSessionId = null
    const endTicket = (handle: PlaybackSessionHandle | null): Promise<void> | undefined =>
      ticketId && ownAccount && (!handle || handle.id !== ticketId) ? endPlaybackSession(ticketId, keepalive) : undefined
    if (keepalive) {
      if (active) void active.close(true)
      else if (opening) void opening.then((handle) => (handle ? handle.close() : undefined)).catch(() => undefined)
      void endTicket(active)
      return
    }
    playbackClosing = playbackClosing.then(() => {
      const settled: Promise<PlaybackSessionHandle | null> = active
        ? Promise.resolve(active)
        : opening || Promise.resolve(null)
      return settled.then((handle) => (handle ? handle.close() : Promise.resolve()).then(() => endTicket(handle)))
    }).catch(() => undefined)
  }

  /**
   * Player-dən çıxış — pultun «Geri»si, ✕ düyməsi, ikiqat klik və bildirişdəki
   * düymə EYNİ yoldan keçir.
   *
   * ⚠ FİLM SƏHİFƏSİNƏ qayıdır, əvvəlki ekrana yox (sahib tələbi 2026-08-04):
   *   hero-dan «İzlə» ilə gələn istifadəçi əvvəl ana ekrana atılırdı.
   *   Yığın məntiqi `app.ts` → `backToDetail`-dədir.
   * ⚠ CANLI İSTİSNADIR: xətti kanalın «film səhifəsi» yoxdur (məzmun cədvəldən
   *   gəlir) — oradan çıxış ana ekranadır.
   */
  function leavePlayer(): void {
    if (options.live) {
      // ⚠ 2026-09-26: canlı ekran player-i `replace` ilə açır (yığın [home, player]); əvvəl
      //   buradan da `replace` ilə home-a gedilirdi → [home, home]: ana ekranda bir Back
      //   ekranı yenidən qururdu (fokus/sürüşmə sıfırlanır), ikincisi kök idi.
      if (stackDepth() > 1) back()
      else navigate({ name: 'home' }, true)
      return
    }
    /**
     * ⚠⚠ HERO-DAN GƏLƏN ANA EKRANA QAYIDIR (sahib tələbi 2026-08-25: «heroda
     *    trailer full screen edib fullscreendən çıxanda filmin modalına yox elə
     *    homepage qayıtmalıdır; filmin modalına o halda qayıtmalıdır ki film
     *    modaldan fullscreen edilsin»).
     *    ƏVVƏL: hero-nun «Tam ekran» düyməsi 2026-08-24-də oynadıcı marşrutuna
     *    bağlandı və çıxış hamı üçün `backToDetail` idi — yəni istifadəçi heç
     *    açmadığı modalı görürdü. Modaldan gələn yol DƏYİŞMƏDİ.
     */
    if (options.fromHero) {
      back()
      return
    }
    /**
     * ⚠ TREYLERDƏ MÖVQE MODALA QAYTARILIR (sahib tələbi 2026-08-21:
     *   «fullscreendən çıxanda da modalda qaldığı yerdən davam etsin»).
     *   Tam filmdə bu göndərilmir — orada davam mövqeyi onsuz da serverdədir
     *   (`progress`), modal isə treyler göstərir.
     */
    const trailerAt =
      options.trailerAsset && isFinite(video.currentTime) ? video.currentTime : undefined
    backToDetail(options.slug, trailerAt)
  }

  /**
   * Oynat/dayandır — idarə sətrindəki düymə, ekrana klik və pult EYNİ yoldan keçir.
   *
   * ⚠ BİLDİRİŞ AÇIQ OLSA DA İŞLƏYİR: bilet alınmayanda treyler bildirişlə BİRLİKDƏ
   *   oynayır (`degradeToTrailer`) — «bildiriş varsa video yoxdur» fərziyyəsi
   *   yanlışdır. Yoxlama mənbənin ÖZÜNƏDİR (`video.src` / Shaka).
   */
  /** hls.js pilləsinin hündürlüyü — `?.` işlətmədən (layihə qaydası, ES2015 həddi). */
  function levelHeight(level: { height?: number } | undefined): number {
    return level && level.height ? level.height : 0
  }

  function togglePlayback(): void {
    // Sıxışdırılmış oynadıcı ölüdür (izah `onSessionReplaced`) — media onsuz da sökülüb.
    if (options.live || replaced) return
    if (!video.src && !shakaPlayer) return
    if (video.paused) {
      // Səhifə gizlənəndə sessiya bitirilmişdi — oynatma yeni sessiya ilə davam edir.
      if (suspended) resumeSession()
      void video.play().catch(() => undefined)
    } else video.pause()
  }

  /**
   * UZUN PAUZA — 5 dəqiqə fasiləsiz pauzadan sonra mövqe yazılır, sessiya bitirilir, heartbeat dayanır
   * (2026-10-25, sahib qərarı 19 — iPhone/tvOS qaydası; hədd və səbəb `lib/pausedSession.ts`-dədir).
   *
   * Kod oxunuşu: ƏVVƏL heartbeat videonun vəziyyətinə baxmırdı — pauzada unudulmuş film saatlarla bir
   * ekran tuturdu (pauza səhifəni gizlətmir, yəni `onPageHidden` işə düşmürdü; televizorun ekran
   * qoruyucusunun `visibilitychange` verib-vermədiyi ölçülməyib). Qayıdış səhifə gizlənməsindən sonrakı eyni yoldur: `suspended` qalxır, «oynat»
   * (`togglePlayback` → `resumeSession`) `/sessions` ilə təzə sessiya açır. Media sökülmür.
   * ⚠ ƏVƏZLƏNMİŞ (409) oynadıcı sayılmır — ölüdür, davam yalnız «Yenidən cəhd et» ilə.
   * ⚠ SIRA iOS-dakı kimidir: əvvəl sessiya bitirilir, sonra mövqe yazılır.
   * ⚠ Canlı (söndürülmüş modul) və treyler bu qaydaya düşmür — onların `/sessions` sessiyası yoxdur.
   */
  function pausedReleaseApplies(): boolean {
    return !destroyed && !options.live && ticketGranted && !replaced && !suspended && video.paused &&
      (playbackSession !== null || playbackOpening !== null || ticketSessionId !== null)
  }

  /** Pauza başladı — sayğac işləyirsə toxunulmur (pauza fasiləsiz sayılır). */
  function schedulePausedRelease(): void {
    if (pausedReleaseTimer !== null) return
    if (!pausedReleaseApplies()) return
    pausedReleaseTimer = window.setTimeout(function () {
      pausedReleaseTimer = null
      if (pausedReleaseApplies()) releaseForLongPause()
    }, PAUSED_SESSION_RELEASE_MS)
  }

  function cancelPausedRelease(): void {
    if (pausedReleaseTimer === null) return
    clearTimeout(pausedReleaseTimer)
    pausedReleaseTimer = null
  }

  function releaseForLongPause(): void {
    suspended = true
    releasePlaybackSession()
    writeProgress(true)
  }

  /**
   * Gizlənmədən (və ya uzun pauzadan) sonra ilk «oynat» — `/sessions` ilə yeni sessiya açılır
   * (sıxışdırma qaydası yalnız bu uçdadır). Media sökülmür: imzalı manifest 6 saat etibarlıdır.
   */
  function resumeSession(): void {
    suspended = false
    const contentId = options.contentId
    const profileId = progressProfileId
    if (!ticketGranted || !contentId || !profileId || options.live) return
    openSession(contentId, profileId)
  }

  /**
   * SƏHİFƏ GİZLƏNİR / BAĞLANIR (`visibilitychange` → hidden · `pagehide`).
   *
   * 2026-10-25, kod oxunuşu: oynadıcı bu hadisələri ümumiyyətlə dinləmirdi. ƏVVƏL tab bağlananda
   * və ya TV-də app fona keçəndə son mövqe (15 saniyəyə qədər) itirdi, sessiya 180 saniyə «canlı»
   * qalıb başqa cihazın yerini tuturdu, fondakı tabda film səslə oynamağa davam edirdi. İndi:
   * mövqe `keepalive` ilə yazılır, video dayandırılır, sessiya bitirilir (sayt `pagehide` ilə
   * eyni). Ekrana qayıdanda heç nə öz-özünə oynamır — sıxışdırılmış oynadıcı ölü qalır,
   * gizlənmiş isə istifadəçi oynadanda yeni sessiya açır (`resumeSession`).
   * ⚠ Canlı (söndürülmüş modul) və treyler rejimində yalnız video dayandırılır.
   */
  function onPageHidden(): void {
    if (destroyed || options.live) return
    if (!video.paused) video.pause()
    // Gizlənmə sessiyanı özü bitirir — uzun pauza sayğacına ehtiyac qalmır.
    cancelPausedRelease()
    if (!ticketGranted || replaced || suspended) return
    writeProgress(true, true)
    suspended = true
    releasePlaybackSession(true)
  }

  function onVisibilityChange(): void {
    if (document.hidden) onPageHidden()
  }

  /**
   * ±10 saniyə — pultun Sol/Sağ oxu, ⏪/⏩ və ekrandakı düymələr eyni funksiyadan keçir.
   *
   * ⚠⚠ BASIŞLAR BİRLƏŞDİRİLİR, HƏR BİRİ AYRICA `currentTime` YAZMIR (547) —
   *   səbəb `SEEK_COMMIT_MS`-in elanındadır (avtotəkrarda 3 saniyə = 33 seek =
   *   33 bufer sıfırlaması). Zolaq və vaxt DƏRHAL hədəfi göstərir, mövqe isə
   *   son basışdan `SEEK_COMMIT_MS` sonra BİR DƏFƏ yazılır.
   * ⚠ Sayğac SIFIRDAN başlamır: ardıcıl basışlar `seekTarget`-in üstünə yığılır,
   *   yəni «10 dəfə sağ» = +100 san (istifadəçinin gözlədiyi davranış).
   */
  let seekPending = false
  let seekStart = 0
  let seekTarget = 0
  let seekTimer: number | null = null

  function seekBy(delta: number): void {
    const duration = video.duration
    if (seekLock || replaced || !duration || isNaN(duration) || !isFinite(duration)) return
    if (!seekPending) {
      seekPending = true
      seekStart = video.currentTime
      seekTarget = video.currentTime
    }
    seekTarget = Math.max(0, Math.min(duration, seekTarget + delta))
    paintProgress(seekTarget)
    showSeekBadge(Math.round(seekTarget - seekStart))
    if (seekTimer !== null) clearTimeout(seekTimer)
    seekTimer = window.setTimeout(commitSeek, SEEK_COMMIT_MS)
    showChrome()
  }

  /** Toplanan sarımanı bir dəfə tətbiq edir. */
  function commitSeek(): void {
    seekTimer = null
    if (!seekPending) return
    seekPending = false
    seekBadge.style.display = 'none'
    if (destroyed) return
    try {
      video.currentTime = seekTarget
    } catch {
      // Bəzi mənbələrdə seek metadata gələnə qədər atılır — zolaq onsuz da
      // növbəti `timeupdate`-də həqiqi mövqeyə qayıdır.
    }
  }

  /** «+40 san» / «−10 san» — sarımanın cəmi. */
  function showSeekBadge(offset: number): void {
    setText(seekBadge, (offset >= 0 ? '+' : '−') + Math.abs(offset) + T(' san'))
    seekBadge.style.display = 'block'
  }

  // ══════════════════════════════════════════════════════════════════════════
  // BAXIŞ MÖVQEYİ — `PUT /api/me/watch-progress` (547)
  // ══════════════════════════════════════════════════════════════════════════
  //
  // ⚠ NİYƏ BU EKRANDA, `lib/playback.ts`-də YOX: yazılacaq dəyər `<video>`
  //   elementindən oxunur və o, yalnız burada var. Səbəb və gövdə izahı
  //   `lib/playback.ts → reportWatchProgress`-dədir.

  /** 15 saniyəlik dövr — bilet alınandan sonra başlayır. */
  function startProgressReports(profileId: string): void {
    // ⚠ «Yenidən cəhd et» ikinci dəfə bilet alır — köhnə dövr dayandırılmasa iki
    //   zəncir yan-yana işləyər və eyni mövqe iki dəfə göndərilərdi.
    stopProgressReports()
    progressProfileId = profileId
    // ⚠ `setInterval` YOX, özünü yenidən planlayan `setTimeout` (canlı drift
    //   döngüsü ilə eyni naxış): TV-də boş interval thrash yaradır.
    const tick = (): void => {
      if (destroyed) return
      writeProgress(false)
      progressTimer = window.setTimeout(tick, PROGRESS_INTERVAL_MS)
    }
    progressTimer = window.setTimeout(tick, PROGRESS_INTERVAL_MS)
  }

  function stopProgressReports(): void {
    if (progressTimer !== null) {
      clearTimeout(progressTimer)
      progressTimer = null
    }
  }

  /**
   * @param force Dövr YOX, son yazı (ekran bağlanır və ya bölüm dəyişir) —
   *   pauzada da yazılır. Dövrdə isə duran video mövqe yazmır (sayt/Android
   *   eyni: dayandırılmış filmə görə server sətri yenilənməməlidir).
   * @param keepalive səhifə gizlənir/bağlanır (`onPageHidden`).
   */
  function writeProgress(force: boolean, keepalive?: boolean): void {
    const profileId = progressProfileId
    const contentId = options.contentId
    if (!profileId || !contentId || !ticketGranted || options.live || !isViewerCurrent()) return
    if (!force && video.paused) return
    const duration = video.duration
    if (!duration || isNaN(duration) || !isFinite(duration)) return
    const position = Math.floor(video.currentTime)
    if (position <= 0 || position === lastReportedSecond) return
    lastReportedSecond = position
    void reportWatchProgress(profileId, contentId, options.episodeId, position, duration, keepalive)
  }

  /** Son yazı — `destroy()`, `ended` və bölüm keçidi bunu çağırır. */
  function flushProgress(): void {
    writeProgress(true)
  }

  // ══════════════════════════════════════════════════════════════════════════
  // «NÖVBƏTİ BÖLÜM» GERİ SAYIMI (547 — sayt `WatchOutro`/`WatchClient` pariteti)
  // ══════════════════════════════════════════════════════════════════════════

  /** Sayım aktivdirmi — pult bu vəziyyətdə YALNIZ karta gedir. */
  let countdownActive = false
  let countdownTimer: number | null = null
  let countdownLeft = 0
  /** 0 = «İndi oynat», 1 = «Ləğv et». */
  let countdownIndex = 0
  /**
   * SONLUQ QATI (dəyərləndirmə + oxşarlar) — `ui/watchOutro.ts`.
   *
   * ⚠ YALNIZ FİLMDƏ: serialda sonluq artıq VAR («növbəti bölüm» avtomatik
   *   keçidi). İkisi eyni anda işləsəydi qat açılan kimi bölüm dəyişər və qat
   *   itərdi — Android TV-də də şərt eynidir (`TvPlayerScreen.kt`).
   * ⚠ TREYLERDƏ YOXDUR: qiymət göndəriləcək kontent istifadəçinin baxdığı film
   *   deyil (treyler «İzlə»nin fallback yoludur).
   */
  let outro: WatchOutro | null = null
  /** Qat bu seansda BİR DƏFƏ açılır — geri sarıyanda təkrar açılmasın. */
  let outroShown = false
  /**
   * Hesab bu filmə ARTIQ qiymət veribmi (`GET /api/me/feedback`).
   *
   * ⚠ SORĞU ALINMAYANDA `true` SAYILIR — saytdakı (`WatchClient.tsx`) eyni
   *   qərar: yanlış təkid etməkdənsə susmaq yaxşıdır.
   */
  let alreadyRated = false
  /**
   * Qat astanadan yox, `ended`-dən açılıb (film HƏQİQƏTƏN bitdi).
   *
   * ⚠ NİYƏ AYRICA BAYRAQ: qat bağlananda ekranda nə qalacağı bu ikisində
   *   FƏRQLİDİR. Astanadan açılıbsa film hələ oynayır — qat sadəcə yığılır.
   *   Film bitibsə arxada DONMUŞ son kadr var: qatı yığıb ekranı orada saxlamaq
   *   dead-end olardı (§6), ona görə player bağlanır — əvvəlki davranış da elə
   *   bu idi (`ended` → `leavePlayer()`).
   */
  let outroAfterEnd = false
  let nextCard: HTMLElement | null = null
  let nextCountNode: HTMLElement | null = null
  let nextPlayBtn: HTMLElement | null = null
  let nextCancelBtn: HTMLElement | null = null

  function buildNextCard(): void {
    if (nextCard) return
    nextCard = el('div', 'player__next')
    nextCard.appendChild(el('div', 'player__next-label', T('Növbəti bölüm')))
    if (upcoming) {
      nextCard.appendChild(
        el(
          'div',
          'player__next-name',
          upcoming.number + T('-ci bölüm') + (upcoming.title ? ' · ' + upcoming.title : ''),
        ),
      )
    }
    nextCountNode = el('div', 'player__next-count', '')
    nextCard.appendChild(nextCountNode)
    const row = el('div', 'player__next-actions')
    nextPlayBtn = el('div', 'btn btn--primary focusable player__next-btn', T('İndi oynat'))
    nextCancelBtn = el('div', 'btn focusable player__next-btn', T('Ləğv et'))
    row.appendChild(nextPlayBtn)
    row.appendChild(nextCancelBtn)
    // Sayım kartı da işarəçi ilə idarə olunur (brauzer variantı) — pult yolu ilə
    // eyni funksiyalar çağırılır.
    if (IS_BROWSER) {
      clickable(nextPlayBtn, () => {
        const target = upcoming
        stopCountdown()
        if (target) switchToEpisode(target)
      })
      clickable(nextCancelBtn, stopCountdown)
    }
    nextCard.appendChild(row)
    page.appendChild(nextCard)
  }

  function paintCountdown(): void {
    if (nextPlayBtn) setClass(nextPlayBtn, 'is-focused', countdownIndex === 0)
    if (nextCancelBtn) setClass(nextCancelBtn, 'is-focused', countdownIndex === 1)
    if (nextCountNode) setText(nextCountNode, countdownLeft + T(' san'))
  }

  /**
   * Sonluq qatını açır (bir dəfə).
   *
   * ⚠ ŞƏRTLƏR ANDROID TV İLƏ EYNİDİR: bilet verilib (tam film), bölüm YOXDUR
   *   (serial deyil) və qat hələ açılmayıb.
   */
  /**
   * «Bu filmə artıq qiymət vermişəmmi» — bilet alınan an soruşulur.
   *
   * ⚠ NİYƏ BURADA, QAT AÇILANDA DEYİL: qat filmin sonuna 30 saniyə qalmış
   *   açılır və o an ŞƏBƏKƏ SORĞUSU GÖZLƏMƏK ulduzların gec çıxmasına və ya
   *   yanıb-sönməsinə səbəb olardı. Cavab dəqiqələrlə əvvəl hazır olur.
   * ⚠ XƏTA = «artıq verilib» (soruşma): saytdakı eyni qərar.
   */
  function loadExistingRating(contentId: string): void {
    void authStatus('/api/me/feedback?itemId=' + contentId)
      .then((response) => {
        if (destroyed) return
        if (response.status !== 200) {
          alreadyRated = true
          return
        }
        try {
          const data = JSON.parse(response.text) as { rating?: number | null }
          alreadyRated = data && data.rating !== null && data.rating !== undefined
        } catch {
          alreadyRated = true
        }
      })
      .catch(() => {
        alreadyRated = true
      })
  }

  /**
   * Sonluq qatından seçilən filmi BİRBAŞA OYNADIR (sahib tələbi 2026-08-23).
   *
   * ⚠ DETAL ÇƏKİLİR, DƏRHAL NAVİQASİYA EDİLMİR: iki şey yalnız detalda var —
   *   serialın BÖLÜMLƏRİ (serialın özünün `cdn_asset_id`-si boşdur, bölümsüz bilet
   *   «video hazır deyil» qaytarardı; hansı bölüm — `resumeEpisode`) və növbəti sonluq qatının
   *   məlumatı (`similar` · `outroSeconds`). `screens/detail.ts`-dəki «İzlə»
   *   düyməsi də eyni qərar ağacından keçir.
   * ⚠ DETAL ALINMASA MODALA DÜŞÜR — boş ekran yaranmır (qırmızı xətt 6).
   */
  function playSimilar(item: ContentCard): void {
    /*
      ⚠⚠ YAŞ QAPISI (2026-10-25, kod oxunuşu). Sonluq xanasının kliki/OK-si birbaşa oynadıcı
         açırdı — qapı yalnız xananın səssiz önizləməsində idi (`ui/watchOutro.ts`). ƏVVƏL
         yaşı çatmayan istifadəçi 18+ təklifə basanda bilet rədd olunur və oynadıcı onun
         treylerini tam ekranda oynadırdı. İndi hökm `#/film/` açılışındakı ilə eynidir
         (`evaluateAge`, yaş yüklənəndən sonra); bağlıdırsa filmin modalı ana ekranın üstündə
         açılır və skinin öz yaş pəncərəsi səbəbi göstərir (`detailOverHome` → seed kart).
    */
    void loadAgeIfNeeded().then(() => {
      if (destroyed) return
      if (evaluateAge(item.ageLimit) !== 'allowed') {
        detailOverHome(item.slug, item)
        return
      }
      openSimilar(item)
    })
  }

  function openSimilar(item: ContentCard): void {
    void getContent(item.slug).then((detail) => {
      if (destroyed) return
      if (!detail) {
        /*
          ⚠ MODAL ANA EKRANIN ÜSTÜNDƏ AÇILIR (sahib tələbi 2026-08-30: «modalı
            bağlayanda ana səhifəyə qayıtsın»). ƏVVƏL `backToDetail` idi — o,
            yığından yalnız BİR sətir götürür, yəni modalın altında BAXILIB
            QURTARMIŞ filmin gəldiyi ekran qalırdı və modal bağlananda istifadəçi
            oynadıcıya geri düşürdü. İzah `app.ts` → `detailOverHome`.
        */
        detailOverHome(item.slug, item)
        return
      }
      // Kartda yaş həddi köhnə/boş ola bilər — detalın həddi ilə qapı bir də yoxlanır.
      if (evaluateAge(detail.ageLimit) !== 'allowed') {
        detailOverHome(detail.slug, Object.assign({}, item, { ageLimit: detail.ageLimit }))
        return
      }
      /*
        ⚠⚠ TAM EKRAN SAXLANILIR — BU, ÖZÜ BİR QÜSURUN DÜZƏLİŞİDİR (2026-08-30,
           TestingAI veb TV 48). NƏ ÖLÇÜLDÜ (canlı veb TV): «Cin Atı» sonluğunda
           ikinci təklif xanasına («Oğlan evi 1») bir dəfə klik edildi və nəticə
           bu oldu — üstdə «Oğlan evi 1»in MODALI açıldı (`.web-dm` hidden=false),
           arxada isə ƏVVƏLKİ film yenidən başlayıb SƏSLİ oynayırdı
           (`.player__title` = «Cin Atı», `.player__times` = 0:08/1:23:27 = 5007 s,
           yəni «Oğlan evi 1»in 100 dəqiqəsi deyil); `/api/catalog/content` və
           `/api/playback/ticket` bir klikə İKİ dəfə getmişdi.
           ZƏNCİR (koddan oxundu, sayğaclarla üst-üstə düşür): `navigate` →
           `app.ts` → `mount()` köhnə ekranı söküb `stopPlayback()`-i çağırır, o da
           `keepFullscreen` qalxmadığı üçün `exitFullscreen()` edir; YENİ player
           elə həmin kadrda qurulur, `enterFullscreen()`-i isə brauzer RƏDD edir
           (çağırış `getContent` cavabının içindədir, artıq «istifadəçi jesti»
           zəncirində deyil); bir az sonra köhnə çıxışın `fullscreenchange`
           hadisəsi gəlir və artıq YENİ player-in `onFullscreenExit` dinləyicisinə
           düşür → `leavePlayer()` → `backToDetail('oglan-evi-1')` → `back()` köhnə
           player marşrutunu geri qurur (2-ci bilet, «Cin Atı» 0:08-dən) və
           `detailOpener` təklifin modalını açır (2-ci `content` sorğusu).
        ⚠ HƏLL BÖLÜM KEÇİDİNİN EYNİSİDİR (`switchToEpisode`) — yeni mexanizm
          yazılmır: bayraq qalxanda köhnə ekran tam ekrandan ÇIXMIR, yəni nə
          rədd olunan `enterFullscreen`, nə də sahibsiz `fullscreenchange` olur.
        ⚠ `navigate(..., true)` = REPLACE (yenilik): PUSH olsaydı yığında
          player-in üstünə player yığılırdı və yeni filmdən çıxanda `backToDetail`
          → `back()` ƏVVƏLKİ FİLMİ yenidən oynadardı (ölçmədəki «arxada Cin Atı
          davam edir» görüntüsünün ikinci yarısı). Bölüm keçidi də elə bu səbəbdən
          `replace`-dir.
      */
      keepFullscreen = true
      // 2026-10-25: serialda ən son yarımçıq bölüm (`resumeEpisode`, sayt `seriesResume.ts`).
      const episode = resumeEpisode(detail.id, detail.episodes)
      if (episode) {
        navigate(
          {
            name: 'player',
            slug: detail.slug,
            title: episodeTitle(detail.title, episode),
            contentId: detail.id,
            episodeId: episode.id,
            episodes: detail.episodes,
            ageLimit: detail.ageLimit,
          },
          true,
        )
        return
      }
      navigate(
        {
          name: 'player',
          slug: detail.slug,
          title: detail.title,
          contentId: detail.id,
          trailerAsset: detail.trailerUrl,
          similar: detail.similar,
          outroSeconds: detail.outroSeconds,
          ageLimit: detail.ageLimit,
        },
        true,
      )
    })
  }

  function openOutro(): void {
    if (destroyed || outroShown || outro) return
    if (!ticketGranted || !options.contentId || options.episodeId) return
    const similar = options.similar || []
    // Nə ulduz, nə təklif varsa qat BOŞ qutu olardı — açılmır (§6).
    if (similar.length === 0 && !options.contentId) return

    outroShown = true
    outro = createWatchOutro({
      /*
        ⚠⚠ QİYMƏT ARTIQ VERİLİBSƏ ULDUZLAR ÇƏKİLMİR (sahib şikayəti 2026-08-23:
           «rating vursan da növbəti dəfə açanda təzə rating istəyir və sanki
           verilməyibmiş kimi»). Serverdə qiymət SAXLANILIRDI — ölçüldü
           (`PUT /api/me/ratings` → 204, sonra `GET /api/me/feedback` → `rating: 8`);
           soruşan client idi. Cavab bilet anında çəkilir (`loadExistingRating`),
           yəni qat açılanda gözləmə yoxdur.
        ⚠ `null` = «ulduz çəkmə» — `createWatchOutro` bu halı onsuz da treyler
          yolu üçün bilirdi, yeni bayrağa ehtiyac yoxdur.
      */
      contentId: alreadyRated ? null : options.contentId,
      similar,
      onOpenSimilar: (item) => {
        /*
          ⚠⚠ BİRBAŞA OYNADILIR, MODAL AÇILMIR (sahib tələbi 2026-08-23: «önərilən
             filmə klik edirsən baxılan filmin modalına qayıdır, birbaşa həmin
             filmi açmalıdır»).

             ƏVVƏL NƏ OLURDU (koddan oxundu): `closeOutro()` → `outroAfterEnd`
             qalxıq olduğu üçün `leavePlayer()` → `backToDetail(options.slug)`,
             yəni İZLƏNƏN filmin modalı; ondan sonra çağırılan
             `backToDetail(item.slug)` isə artıq ikinci keçid idi. Ona görə
             bayraq əvvəlcə endirilir.
          ⚠ HÜQUQ VƏ YAŞ SERVERDƏDİR: yeni oynatma öz biletini istəyir
            (`attachFullContent`), 403/yaş halında isə player öz bildirişini
            göstərir — yəni «öncəki filmi birdəfəlik alıb» halı da avtomatik
            düzgün işləyir (yeni film üçün hüquq yoxdursa ödəniş/bildiriş yolu).
        */
        outroAfterEnd = false
        closeOutro()
        playSimilar(item)
      },
      onFinished: closeOutro,
    })
    page.appendChild(outro.node)
    // Chrome görünsün ki, istifadəçi qatı və qalan vaxtı birlikdə görsün.
    showChrome()
  }

  function closeOutro(): void {
    if (!outro) return
    outro.destroy()
    outro = null
    // İzah `outroAfterEnd` elanındadır.
    if (outroAfterEnd && !destroyed) leavePlayer()
  }

  /**
   * Mövqe yoxlaması — qat filmin bitməsinə [OUTRO_DEFAULT_SECONDS] (və ya
   * redaksiya dəyəri) qalmış açılır.
   *
   * ⚠ ASTANA MÜDDƏTİN YARISINDAN ÇOX OLA BİLMƏZ: qısa metrajda (məs. 90
   *   saniyəlik çarx filmi) 40 saniyəlik dəyər qatı filmin ORTASINDA açardı.
   *   Baza `<= 600` yoxlayır (V064), amma o, MÜTLƏQ həddir — nisbi hədd yalnız
   *   burada bilinir, çünki müddət yalnız oynadıcıda məlumdur. Saytda da eyni
   *   düzəliş var (`WatchClient.tsx → outroLeadSeconds`).
   */
  function checkOutro(): void {
    if (outroShown || destroyed || options.live) return
    const duration = video.duration
    if (!duration || isNaN(duration) || !isFinite(duration)) return
    const wanted =
      options.outroSeconds && options.outroSeconds > 0 ? options.outroSeconds : OUTRO_DEFAULT_SECONDS
    const lead = Math.min(wanted, duration / 2)
    if (duration - video.currentTime > lead) return
    openOutro()
  }

  function startNextCountdown(): void {
    if (destroyed || !upcoming || countdownActive) return
    buildNextCard()
    countdownActive = true
    countdownIndex = 0
    countdownLeft = NEXT_EPISODE_SECONDS
    if (nextCard) nextCard.style.display = 'block'
    // Bölüm bitib — «pauza» nişanı burada yanlış siqnaldır (istifadəçi
    // dayandırmayıb, film qurtarıb).
    pausedBadge.style.display = 'none'
    // Chrome görünsün ki, istifadəçi kartı və vaxtı birlikdə görsün.
    showChrome()
    paintCountdown()
    const tick = (): void => {
      if (destroyed || !countdownActive) return
      countdownLeft -= 1
      if (countdownLeft <= 0) {
        stopCountdown()
        if (upcoming) switchToEpisode(upcoming)
        return
      }
      paintCountdown()
      countdownTimer = window.setTimeout(tick, 1000)
    }
    countdownTimer = window.setTimeout(tick, 1000)
  }

  function stopCountdown(): void {
    countdownActive = false
    if (countdownTimer !== null) {
      clearTimeout(countdownTimer)
      countdownTimer = null
    }
    if (nextCard) nextCard.style.display = 'none'
  }

  /** `Object.assign` Chrome 45+-dır (webOS 3.0 = Chrome 38-də yoxdur) — əl ilə köçürülür. */
  function copyHeaders(target: Record<string, string>, source: Record<string, string> | null | undefined): void {
    if (!source) return
    for (const key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) target[key] = source[key] as string
    }
  }

  function stopPlayback(): void {
    // ⚠ SON MÖVQE YAZISI DESTROY-DAN ƏVVƏLDİR (547): aşağıda `video.src`
    //   götürülür və `currentTime` sıfırlanır — ondan sonra yazılacaq dəyər yoxdur.
    flushProgress()
    destroyed = true
    cancelPausedRelease()
    window.removeEventListener('storage', onViewerStorage)
    window.removeEventListener('focus', checkViewer)
    window.removeEventListener('pagehide', onPageHidden, false)
    document.removeEventListener('visibilitychange', onVisibilityChange, false)
    playbackAttempt += 1
    releasePlaybackSession()
    if (livePollTimer !== null) window.clearTimeout(livePollTimer)
    closeOutro()
    stopCountdown()
    if (seekTimer !== null) {
      clearTimeout(seekTimer)
      seekTimer = null
    }
    stopProgressReports()
    // ⚠ BÖLÜM KEÇİDİNDƏ TAM EKRANDAN ÇIXILMIR — səbəb `keepFullscreen` elanındadır.
    if (!keepFullscreen) exitFullscreen()
    if (liveHeartbeatTimer !== null) {
      clearTimeout(liveHeartbeatTimer)
      liveHeartbeatTimer = null
    }
    if (liveTimer !== null) {
      clearTimeout(liveTimer)
      liveTimer = null
    }
    if (chromeTimer !== null) {
      clearTimeout(chromeTimer)
      chromeTimer = null
    }
    if (bannerTimer !== null) {
      clearTimeout(bannerTimer)
      bannerTimer = null
    }
    if (hls) {
      hls.destroy()
      hls = null
    }
    if (shakaPlayer) {
      void shakaPlayer.destroy()
      shakaPlayer = null
    }
    video.removeAttribute('src')
    // `load()` olmadan bəzi TV-lərdə dekoder buraxılmır və növbəti oynatma
    // «no free decoder» ilə uğursuz olur.
    video.load()
  }

  // 2026-10-13: başqa tabda uşaq profilinə keçid əvvəlki böyük videosunu
  // dayandırmırdı. Yeni profil öz bileti və yaş yoxlamasından keçməlidir.
  function checkViewer(): void {
    if (destroyed || isViewerCurrent()) return
    stopPlayback()
    navigate(readSession() ? { name: 'profiles', replaceOnPick: true } : { name: 'qr' }, true)
  }
  function onViewerStorage(event: StorageEvent): void {
    if (event.key === null || event.key === 'kb_tv_session' || event.key === 'kb_tv_session_context'
      || event.key === 'kb_tv_profile') checkViewer()
  }
  window.addEventListener('storage', onViewerStorage)
  window.addEventListener('focus', checkViewer)
  // Gizlənmə/bağlanma — izah `onPageHidden`-dədir.
  window.addEventListener('pagehide', onPageHidden, false)
  document.addEventListener('visibilitychange', onVisibilityChange, false)

  return {
    node,
    takeFocus() {
      // Player-də fokus halqası yoxdur — düymələr birbaşa pultdadır.
    },
    releaseFocus() {},
    onKey(key: RemoteKey) {
      // Hər düymə basılışı chrome-u geri qaytarır və 5 saniyəlik sayğacı sıfırlayır —
      // istifadəçi harada olduğunu (timeline) istədiyi an görə bilir.
      showChrome()

      /**
       * PARAMETRLƏR PANELİ AÇIQDIRSA bütün pult ora aiddir.
       *
       * ⚠ Ən yuxarıda dayanır: aşağıdakı `left/right` sarıması və `enter`
       *   oynat/dayandırı panel açıqkən İŞLƏMƏMƏLİDİR — istifadəçi siyahıda
       *   gəzərkən film sarınsaydı bu, «app öz-özünə nəsə edir» kimi oxunardı.
       */
      if (panelOpen) {
        if (key === 'up') movePanel(-1)
        else if (key === 'down') movePanel(1)
        else if (key === 'enter' || key === 'playpause') runPanel()
        else if (key === 'back' || key === 'left') closePanel()
        return
      }

      /**
       * SONLUQ QATI AÇIQDIRSA pult ƏVVƏLCƏ ORA gedir (2026-08-21).
       *
       * ⚠ QAT HƏR DÜYMƏNİ UDMUR: `onKey` `false` qaytaranda (oynat/dayandır,
       *   sarıma) idarə aşağıdakı adi məntiqə keçir — film hələ oynayır və
       *   istifadəçini oynadıcıdan ayırmaq düzgün deyil.
       */
      if (outro && outro.onKey(key)) return

      /**
       * «NÖVBƏTİ BÖLÜM» SAYIMI AÇIQDIRSA pult YALNIZ karta gedir (547).
       *
       * ⚠ Paneldən SONRA, qalan hər şeydən ƏVVƏL: sayım vaxtı film onsuz da
       *   bitib, yəni sarıma/oynat-dayandır mənasızdır və istifadəçinin
       *   basdığı düymə keçidi ləğv etməli və ya tezləşdirməlidir.
       * ⚠ GERİ = «Ləğv et» (çıxış YOX): sayımı dayandırmaq istəyən adam player-dən
       *   çıxmaq istəmir. Kart bağlandıqdan sonra növbəti Geri onsuz da çıxarır.
       */
      if (countdownActive) {
        if (key === 'left') {
          countdownIndex = 0
          paintCountdown()
        } else if (key === 'right') {
          countdownIndex = 1
          paintCountdown()
        } else if (key === 'enter' || key === 'playpause') {
          const target = upcoming
          stopCountdown()
          if (countdownIndex === 0 && target) switchToEpisode(target)
        } else if (key === 'next') {
          const target = upcoming
          stopCountdown()
          if (target) switchToEpisode(target)
        } else if (key === 'back' || key === 'stop') {
          stopCountdown()
        }
        return
      }

      /**
       * BİLDİRİŞ AÇIQDIRSA pult onun düymələrinə gedir (547).
       *
       * ⚠ ƏVVƏL BLOKUN DÜYMƏSİ FOKUS ALMIRDI: `focusable` sinfi vardı, amma heç
       *   vaxt `is-focused` qoyulmurdu və Enter birbaşa çıxışa gedirdi — yəni
       *   «Yenidən cəhd et» kimi ikinci bir aksiya ƏLÇATMAZ olardı.
       * ⚠ GERİ HƏLƏ DƏ ÇIXIŞDIR (aşağıdakı blok) — bildiriş istifadəçini
       *   ekranda kilidləməməlidir (§6).
       */
      if (notice && (key === 'left' || key === 'right')) {
        const items = noticeItems()
        noticeIndex =
          key === 'right' ? Math.min(noticeIndex + 1, items.length - 1) : Math.max(noticeIndex - 1, 0)
        paintNotice()
        return
      }

      // CANLI fullscreen player bir rail bölməsi kimidir (ora `replace` ilə gəlinir, stack=1).
      // ⚠ KİLİD DÜZƏLİŞİ: play/pause/seek bloklu, amma naviqasiya AÇIQ olmalıdır —
      //   Sol → menyu (rail), Geri → ana səhifə. Əks halda istifadəçi player-də ilişir.
      if (options.live) {
        if (key === 'left') {
          focusRail()
          return
        }
        if (key === 'back' || key === 'stop') {
          leavePlayer()
          return
        }
        return
      }
      if (key === 'back' || key === 'stop') {
        /**
         * ⚠⚠ GERİ İLK BASIŞDA ÇIXIR (MYTasks 549: «veb tv player esc basanda
         *   dönüş etməlidir ilk dəfədən amma etmir»).
         *
         *   ƏVVƏLKİ QAYDA (522, sahib tələbi 2026-08-04: «player funksiyaları
         *   ekrandan itməyibsə pultda back basanda itsin») chrome GÖRÜNƏN hər
         *   halda birinci Back-i UDURDU. Chrome isə player açılan kimi 5 saniyə
         *   görünür və HƏR düymə basılışı onu geri qaytarır — yəni praktikada
         *   istifadəçi əksər hallarda iki dəfə basmalı olurdu.
         *
         *   İNDİ ŞƏRT DARALDILIB: Back yalnız idarə sətri HƏQİQƏTƏN FOKUSDA
         *   olanda onu tərk edir. Bu, Android TV-dəki davranışın DAHA DƏQİQ
         *   qarşılığıdır: orada Media3 idarə paneli görünəndə fokus HƏMİŞƏ
         *   onun içindədir (`PlayerView.isDpadKey` fokusu ora salır), yəni
         *   `BackHandler`-in «controlsOnScreen» şərti elə «sətir fokusdadır»
         *   deməkdir. Burada isə sətir fokus almadan da görünə bilir — həmin
         *   halda Back-i udmaq istifadəçi üçün «düymə işləmir» deməkdir.
         * ⚠ `stop` HƏMİŞƏ ÇIXIR — media düyməsinin mənası «oynatmanı bitir»dir.
         * ⚠ Brauzerin Esc-i udması AYRI qüsurdur və `onFullscreenExit` ilə
         *   həll olunub (yuxarıdakı qeyd) — ikisi birlikdə «ilk basışda çıxış»
         *   verir.
         */
        if (key === 'back' && controlIndex >= 0) {
          leaveControlRow()
          hideChrome()
          return
        }
        leavePlayer()
        return
      }
      // Bildiriş açıqdırsa OK seçilmiş düyməni işlədir («Yenidən cəhd et» / «Geri»).
      // ⚠ Əvvəl OK birbaşa çıxışa gedirdi — indi blokun öz fokusu var (547).
      if (notice && (key === 'enter' || key === 'playpause')) {
        const items = noticeItems()
        const item = items[noticeIndex]
        if (item) item.run()
        else leavePlayer()
        return
      }
      /**
       * ⚠⚠ İDARƏ SƏTRİ OYNAT/DAYANDIRDAN ƏVVƏL GƏLİR (547-də tapılmış REAL QÜSUR,
       *   brauzerdə ölçüldü 2026-08-07).
       *
       *   Blok əvvəl AŞAĞIDA idi və üstündəki «enter → togglePlayback» qaydası
       *   OK-u həmişə udurdu. Nəticə: sətrin heç bir düyməsi işə düşmürdü, yəni
       *   ⚙ (keyfiyyət · altyazı · səs · sürət) paneli PULTLA ÜMUMİYYƏTLƏ
       *   AÇILMIRDI — yuxarı ox 2026-08-06-da götürüləndən sonra ondan başqa yol
       *   qalmamışdı. Ekranda fokus halqası ⚙-in üstündə görünürdü, OK isə filmi
       *   dayandırırdı: sahibin «player özəllikləri zəifdir funksional deyil»
       *   şikayətinin ölçülə bilən mərkəzi məhz budur.
       * ⚠ MEDIA düymələri (⏯/▶/⏸) BURADA TUTULMUR — onlar sətir fokusda olsa da
       *   oynatmanı idarə etməlidir (aşağıdakı blok), çünki pultun ayrıca
       *   düymələridir və hədəfləri açıqdır.
       */
      if (controlIndex >= 0) {
        const items = controlItems()
        if (key === 'right') { controlIndex = Math.min(controlIndex + 1, items.length - 1); paintControlFocus(); return }
        if (key === 'left') { controlIndex = Math.max(controlIndex - 1, 0); paintControlFocus(); return }
        if (key === 'up') { leaveControlRow(); return }
        if (key === 'enter') {
          const item = items[controlIndex]
          leaveControlRow()
          if (item) item.run()
          return
        }
      }
      if (!options.live && (key === 'playpause' || key === 'enter')) {
        // Native, hls.js və Shaka — üçü də `<video>` element vasitəsilə oynat/dayandır.
        togglePlayback()
        return
      }
      // ⚠ ▶ və ⏸ AYRI DÜYMƏLƏRDİR (2026-09-26): əvvəl hər ikisi toggle idi — oynayarkən
      //   ▶ basmaq filmi dayandırırdı, dayanmışkən ⏸ basmaq oynadırdı.
      if (!options.live && key === 'play') {
        if (video.paused) togglePlayback()
        return
      }
      if (!options.live && key === 'pause') {
        if (!video.paused) togglePlayback()
        return
      }
      // ⚠ CANLI: play/pause/seek bloklu (passiv baxış — drift döngüsü playhead-i saxlayır).
      //   Seans (liveSession) rejimində də seek qadağandır (§5.13) — `seekBy` özü yoxlayır.
      // ±10 san sarımaq (treyler + tam film; Shaka `video.currentTime`-i idarə edir).
      if (key === 'right') seekBy(10)
      if (key === 'left') seekBy(-10)
      /**
       * ⏪/⏩ PULT DÜYMƏLƏRİ — ox düymələri ilə EYNİ funksiya.
       *
       * ⚠ Bu düymələr `registerPlatformKeys`-də Tizen-dən İSTƏNİRDİ, amma
       *   `keys.ts`-dəki kod cədvəlində qarşılığı yox idi (412/417) — yəni pultda
       *   basılırdı və HEÇ NƏ olmurdu. Sahib bildirişi: «player funksional
       *   olmalıdır, yarımçıq yarımçıq etmisən».
       */
      if (key === 'forward') seekBy(10)
      if (key === 'rewind') seekBy(-10)
      /**
       * «NÖVBƏTİ TREK» DÜYMƏSİ → birbaşa növbəti bölüm (522; Android
       * `TvPlayerScreen` → `KEYCODE_MEDIA_NEXT`: «düyməsi olan pultlarda
       * BİRBAŞA keçid — menyu açmadan»; düyməsi olmayanlar üçün eyni aksiya
       * ⚙ panelindədir). Şərtlər (`upcoming` · bilet · təkrar basış)
       * `goToNextEpisode`-un özündədir.
       */
      if (key === 'next') {
        goToNextEpisode()
        return
      }
      /**
       * «ƏVVƏLKİ TREK» DÜYMƏSİ → əvvəlki bölüm (547).
       *
       * ⚠ ETALONLARDA YOXDUR və bu, ORADAKI BOŞLUQDUR: Android TV-də düymə
       *   Media3-ün `seekToPrevious()`-una düşür və tək bəndlik siyahıda sadəcə
       *   0-a sarıyır; saytda isə keçid player-in altındakı bölüm siyahısındandır.
       *   Pultda NEXT işləyib PREV işləməsə istifadəçi bunu nasazlıq sayır.
       */
      if (key === 'prev') {
        if (preceding) switchToEpisode(preceding)
        return
      }
      /**
       * AŞAĞI OX → idarə sətri (Media3-dəki davranışın eynisi).
       *
       * ⚠ NİYƏ AŞAĞI: sətir ekranın ALTINDADIR — istiqamət göz üçün təbiidir və
       *   yuxarı ox (əvvəllər paneli açan düymə) artıq boşdur, yəni köhnə
       *   vərdişlə təsadüfən parametrlərə düşmək mümkün deyil.
       */
      if (key === 'down' && !options.live) {
        controlIndex = 0
        paintControlFocus()
        return
      }
      /**
       * ⚠⚠ YUXARI OX ARTIQ PANELİ AÇMIR (sahib tələbi 2026-08-06, MYTasks 497/498:
       *   «keyfiyyət… SEÇİMLƏ açılmalıdır, yoxsa ki üst oxu basan kimi quality
       *   açılır və bu yanlışdır»). Yuxarı ox adi naviqasiyadır — panel onu
       *   udanda istifadəçi təsadüfən parametrlərə düşürdü.
       * ⚠ Funksiya İTMİR: ⚙ düyməsi idarə sətrinin ƏN SAĞINDADIR (`menuButton`,
       *   569-dan sonra ekranın sağ kənarında) və pultla fokuslanıb OK ilə açılır
       *   — Android TV-də qurulan davranışın eynisi.
       */
    },
    destroy() {
      // ⚠ Dinləyici MÜTLƏQ götürülür: `document`-ə bağlıdır, ekranla birlikdə
      //   ölmür. Qalsaydı hər player açılışında biri də üstünə yığılardı.
      // ⚠ MÜTLƏQ TƏMİZLƏNİR: sinif `body`-dədir, ekranla birlikdə ölmür —
      //   qalsaydı player-dən çıxandan sonra BÜTÜN app-da kursor gizli qalardı.
      setClass(document.body, 'is-player-idle', false)
      // ⚠ TAM EKRAN DİNLƏYİCİSİ (549) — `document`-ə bağlıdır, ekranla birlikdə
      //   ölmür. Qalsaydı bölümdən bölümə keçdikcə üst-üstə yığılar və hər biri
      //   `leavePlayer()` çağırardı.
      if (detachFullscreen) {
        detachFullscreen()
        detachFullscreen = null
      }
      leaveControlRow()
      stopPlayback()
    },
  }
}
